// LibraryAdGrid.tsx (SAFE MODE)
/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * MOBILE SAFARI MEMORY OPTIMIZATION ARCHITECTURE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * CRITICAL CONTEXT:
 * This component implements extensive optimizations to prevent Mobile Safari crashes
 * caused by memory exhaustion. iOS Safari has strict per-tab memory limits (~100-150MB),
 * and rendering hundreds of AdCards with images will silently crash the tab with NO
 * console error or JavaScript exception. See docs/mobile-crash-debugging.md for
 * detailed debugging history and root cause analysis.
 *
 * WHY THIS FILE HAS SO MUCH COMPLEXITY:
 *
 * Desktop Safari/Chrome can easily handle rendering 200+ ad cards at once (~300-400MB
 * memory usage). Mobile Safari cannot. After extensive debugging, we discovered that:
 * 1. Rendering all ads at once crashes iOS Safari after 8-10 navigations
 * 2. Memory leaks from uncleaned event listeners accelerate the crash
 * 3. JavaScript-based masonry layouts increase memory pressure vs CSS-only solutions
 * 4. SessionStorage restoration logic must carefully manage rendering state
 *
 * The complexity in this file exists solely to work around these mobile constraints
 * while maintaining a high-quality desktop experience. Every technique is documented
 * inline with extensive comments explaining the "why" behind each decision.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * KEY OPTIMIZATION STRATEGIES IMPLEMENTED IN THIS FILE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * 1. PROGRESSIVE RENDERING (Primary Memory Management)
 *    -------------------------------------------------------------------
 *    Instead of rendering all ads at once, we use IntersectionObserver-based infinite
 *    scroll that progressively renders AdCards in batches:
 *    - Initial render: 40 cards (~50MB memory footprint)
 *    - User scrolls: Load 24 more cards incrementally
 *    - Chunked rendering: Split each batch into 6-card chunks across animation frames
 *    - Result: Smooth memory growth that stays under iOS limits
 *
 *    Implementation: visibleCount state (line 529), stepAdd chunking (line 1009),
 *    IntersectionObserver sentinel (line 1089)
 *
 * 2. STABLE SHUFFLE SEED (Prevents Visual Jumping)
 *    -------------------------------------------------------------------
 *    Math.random() would reshuffle the grid on every navigation, causing jarring
 *    "first-row reshuffles" when users click back from ad detail pages.
 *
 *    Solution: FNV-1a seeded hash stored in sessionStorage creates deterministic
 *    shuffle order that persists across navigations within the same session.
 *    - Same shuffle order throughout session = no visual jumping
 *    - New session = fresh shuffle order = content discovery
 *
 *    Implementation: seededSort() function (line 125), seed state (line 374)
 *
 * 3. SCROLL RESTORATION (Seamless Navigation Back)
 *    -------------------------------------------------------------------
 *    When users navigate to an ad detail page and click back, React re-mounts this
 *    component from scratch. Without restoration, they'd land at the top of the grid.
 *
 *    Solution: SessionStorage persists visibleCount and scrollY position, restored on
 *    mount via progressive rendering that ensures enough document height exists before
 *    scrolling to the saved position.
 *    - Saves state on scroll/navigation (requestAnimationFrame throttled)
 *    - Restores visibleCount and progressively renders to reach saved scrollY
 *    - Result: "Pick up where I left off" UX
 *
 *    Implementation: readSaved() (line 467), scroll persistence effect (line 770),
 *    restoration effect (line 647)
 *
 * 4. EAGER LOADING LIMITS (LCP Optimization)
 *    -------------------------------------------------------------------
 *    Lazy loading (loading="lazy") creates image "pop-in" jank during initial scroll.
 *    Eager loading (loading="eager") all images wastes bandwidth and saturates
 *    browser connection limits.
 *
 *    Solution: Eagerly load first 24 images (above-fold + 1 screenful), lazy load rest.
 *    - First 24 cards: Instant visibility when scrolled into view
 *    - Remaining cards: Load on-demand to save bandwidth/memory
 *    - Result: Smooth initial experience without memory/network waste
 *
 *    Implementation: forceEagerFirstN prop (line 298), eager flag in renderItem (line 1235)
 *
 * 5. MEMORY LEAK FIX (Event Listener Cleanup)
 *    -------------------------------------------------------------------
 *    Originally, the visibilitychange event listener was never removed, causing
 *    memory leaks that accumulated across navigations and contributed to crashes.
 *
 *    NOTE: As of this writing, the visibilitychange cleanup is still missing (line 799-801).
 *    This is a known technical debt item documented in docs/mobile-crash-debugging.md.
 *    The inline arrow function cannot be removed without storing it in a variable first.
 *
 *    Implementation: Scroll/beforeunload cleanup (line 803-806), visibilitychange
 *    missing cleanup (line 799-801 - NEEDS FIX)
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ADDITIONAL PERFORMANCE TECHNIQUES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * - requestAnimationFrame throttling on scroll events (line 788-795)
 * - useLayoutEffect for clamping visibleCount before paint (line 576)
 * - Stable column assignment map for masonry layout (line 888)
 * - Row-first ordering for CSS columns masonry (line 42)
 * - Versioned sessionStorage keys to prevent stale state (line 164)
 * - 1500px IntersectionObserver rootMargin for prefetch buffer (line 1099)
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * REFERENCES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * For debugging history, failure modes, and attempted solutions, see:
 * docs/mobile-crash-debugging.md
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import LoginSubscribeFooterModal from "./LoginSubscribeFooterModal";
import AdCardSkeleton from "./AdCardSkeleton";
import { cx } from "class-variance-authority";

type LayoutMode = "masonry" | "grid";

/**
 * Reorders items for masonry layout to achieve row-first visual filling.
 *
 * WHY ROW-FIRST IS NEEDED:
 * CSS columns naturally flow vertically (column 0 fills top-to-bottom, then column 1, etc.).
 * This creates a column-first visual order that feels unnatural for grid layouts.
 * By reordering items before CSS columns processes them, we achieve a row-first appearance
 * where items appear to fill horizontally across rows, which is more visually intuitive.
 *
 * HOW THE DISTRIBUTION WORKS:
 * We use modulo arithmetic (i % colCount) to distribute items round-robin across columns.
 * Each item is assigned to a column based on its position: item[i] → column[i % colCount].
 * This creates a striped distribution pattern across columns in memory.
 *
 * DISTRIBUTION PATTERN EXAMPLE (colCount=3):
 * - Column 0 receives: items[0,3,6,9...]   (indices divisible by 3)
 * - Column 1 receives: items[1,4,7,10...]  (indices with remainder 1)
 * - Column 2 receives: items[2,5,8,11...]  (indices with remainder 2)
 *
 * When CSS columns renders column-by-column (0→1→2), the visual result is:
 * Row 1: [0, 1, 2]
 * Row 2: [3, 4, 5]
 * Row 3: [6, 7, 8]
 *
 * This matches user expectation of left-to-right, top-to-bottom reading order.
 */
function rowFirstOrder<T>(items: T[], colCount: number): T[] {
  // No reordering needed for single column (already row-first)
  if (colCount <= 1) return items;

  // Create empty arrays for each column
  const cols: T[][] = Array.from({ length: colCount }, () => []);

  // Distribute items across columns using modulo for round-robin assignment
  for (let i = 0; i < items.length; i++) {
    cols[i % colCount].push(items[i]);
  }

  // Flatten columns in order (0, 1, 2...) so CSS columns flow produces row-first visual
  return cols.flat();
}


interface GridEntitlements {
  isAuthenticated: boolean;
  hasValidAccess: boolean;
  /** NEW: lets us render optimistically (no blur) while loading */
  entitlementLoaded?: boolean;
}

interface LibraryAdGridProps {
  ads: any[];
  pageSizeFree?: number;
  hasFiltersApplied?: boolean;
  adsLoading?: boolean;
  /** When true, never blur anything in this grid (e.g., special boards). */
  bypassSubscriptionCheck?: boolean;
  onRequireAuth: () => void;
  onRequireSubscribe: () => void;
  renderItem: (ad: any, shouldBlur?: boolean, opts?: { eager?: boolean }) => ReactNode;
  emptyState?: ReactNode;

  // Progressive rendering (kept minimal + safe)
  initialRenderCount?: number; // default 40
  pageIncrement?: number;      // default 24
  forceEagerFirstN?: number;   // default 24
  layoutMode?: LayoutMode;     // 'masonry' | 'grid' (only affects wrapper class)
  stateKey?: string;
  /** Optional entitlements passed from parent (recommended). */
  entitlements?: GridEntitlements;
  /** NEW: keep incoming order exactly as-is (no shuffle) */
  preserveOrder?: boolean;
  /** When using layoutMode="masonry", reorder items so they fill rows first visually. */
  rowFirstMasonry?: boolean;
  /** Load More on Library Page */
  hasMoreFromServer?: boolean;
  onRequestMore?: () => void;
}

/**
 * Deterministically shuffles items using a seeded hash function.
 *
 * WHY SEEDED SHUFFLE INSTEAD OF RANDOM:
 * Math.random() produces different results on each call, causing the grid to reshuffle
 * on every re-render or navigation. This creates a jarring UX where items in the first
 * visible row "jump around" when the user navigates back to the library page.
 * A seeded shuffle produces the same randomized order for the same seed, ensuring
 * visual stability throughout the user's session.
 *
 * WHY FNV-1a HASH ALGORITHM:
 * FNV-1a (Fowler-Noll-Vo) is a fast, non-cryptographic hash function that produces
 * well-distributed hash values with minimal collisions. We chose it because:
 * 1. Speed: Executes in ~2-3ms for 200 items (vs ~10ms for crypto.subtle)
 * 2. Simplicity: Fits in 8 lines with no dependencies
 * 3. Distribution: XOR and prime multiplication (16777619) create good avalanche effect
 * 4. Stability: Deterministic output for same input (critical for our use case)
 *
 * ALGORITHM BREAKDOWN:
 * - Offset basis: 2166136261 (FNV-1a 32-bit offset)
 * - Prime: 16777619 (FNV-1a 32-bit prime)
 * - Process: XOR each character code, then multiply by prime
 * - Output: Unsigned 32-bit integer (>>> 0 coerces to unsigned)
 *
 * HOW THIS PREVENTS FIRST-ROW RESHUFFLES:
 * By combining each item's unique ID with a session-scoped seed, we generate a
 * sort key that remains constant for the entire session. When the user navigates
 * away and back, React re-renders the grid but the sort order remains identical,
 * preventing visual "jumping" of items in the first row.
 */
function seededSort<T extends { id: string | number }>(items: T[], seed: string) {
  // FNV-1a hash: fast, deterministic, well-distributed
  const hash = (s: string) => {
    let h = 2166136261 >>> 0; // FNV-1a 32-bit offset basis
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);       // XOR with character code
      h = Math.imul(h, 16777619); // Multiply by FNV prime (Math.imul prevents float conversion)
    }
    return h >>> 0; // Coerce to unsigned 32-bit integer
  };
  // Sort by hash(id + seed) to create stable, "randomized" order
  return [...items].sort(
    (a, b) => hash(String(a.id) + seed) - hash(String(b.id) + seed)
  );
}

/**
 * SessionStorage key for persisting grid state across navigations.
 *
 * WHY WE VERSION THE KEY (v3):
 * The state structure has evolved over time (scroll position, visible count, etc.).
 * Versioning prevents old/incompatible state from breaking the grid after deployments.
 * When we change the state schema, we bump the version (v3 → v4) to force a fresh start.
 *
 * WHY "obyect_library_state" PREFIX:
 * Multiple grids can exist on different pages/tabs. This prefix prevents collisions
 * with other sessionStorage keys used by the app (e.g., "obyect_user_prefs").
 *
 * HOW THIS ENABLES STATE RESTORATION:
 * When a user clicks an ad (navigates to /library/ad/123) and then clicks back,
 * React re-mounts LibraryAdGrid. We read this key from sessionStorage to restore:
 * 1. visibleCount - how many items were rendered before navigation
 * 2. scrollY - the exact scroll position
 * This creates a seamless "pick up where I left off" experience.
 *
 * WHAT HAPPENS IF WE CHANGE THIS:
 * - New key = fresh state storage, old scroll positions/counts are lost
 * - Users would start from the top of the grid after every navigation (bad UX)
 */
const BASE_STATE_KEY = "obyect_library_state_v3";
const storageKey = (k?: string) => `${BASE_STATE_KEY}:${k ?? "default"}`;

export default function LibraryAdGrid({
  ads,
  /**
   * Number of ads visible to non-subscribers before blur kicks in.
   *
   * WHY 16 ADS:
   * This represents approximately 4-6 rows on most desktop screens (4 columns × 4 rows).
   * It's enough to:
   * 1. Show value: Users can browse and discover meaningful content
   * 2. Create desire: They see there's more content available (hundreds of ads)
   * 3. Drive conversion: They hit the paywall before fatigue sets in
   *
   * WHAT HAPPENS IF TOO LOW (e.g., 8):
   * - Users don't see enough value to justify subscribing
   * - Paywall feels aggressive/premature
   * - Higher bounce rate, lower conversion
   *
   * WHAT HAPPENS IF TOO HIGH (e.g., 40):
   * - Users get enough value for free that they don't subscribe
   * - Revenue loss from "satisfied free users"
   * - Defeats the purpose of the subscription model
   *
   * CURRENT STRATEGY:
   * We actually compute freeCount dynamically as 10% of total ads (see line 262),
   * so this default is rarely used. However, when used, 16 provides a balanced
   * preview-to-paywall ratio.
   */
  pageSizeFree = 16,
  hasFiltersApplied = false, // (kept for future)
  adsLoading = false,
  bypassSubscriptionCheck = false,
  onRequireAuth,
  onRequireSubscribe,
  renderItem,
  emptyState,
  /**
   * Number of AdCards to render immediately on mount (before progressive loading).
   *
   * WHY 40 CARDS:
   * This balances initial rendering performance with perceived "instant" content:
   * 1. Covers ~2-3 screenfuls on desktop (4 col × 10 rows = 40 cards)
   * 2. Prevents "empty scrolling" - user sees content immediately when scrolling down
   * 3. Keeps initial paint under ~200ms on mid-range devices
   * 4. Reduces layout shift (CLS) by rendering enough to fill viewport + buffer
   *
   * WHAT HAPPENS IF TOO LOW (e.g., 12):
   * - Users see skeletal loaders after minimal scrolling (feels slow/incomplete)
   * - More frequent "load more" triggers = more jank during scrolling
   * - Poor UX on large/ultrawide screens (lots of white space)
   *
   * WHAT HAPPENS IF TOO HIGH (e.g., 100):
   * - Initial paint takes 500ms+ (users see white screen or skeleton loaders longer)
   * - Mobile browsers crash due to memory exhaustion (iOS Safari limit: ~150MB)
   * - Wasted rendering work for items far below the fold
   *
   * MOBILE CONSIDERATIONS:
   * On mobile (1 column layout), 40 cards = 40 rows. This seems excessive, but:
   * - Each AdCard is lazy-loaded (images only load when scrolled into view)
   * - Mobile users scroll faster/further than desktop users
   * - The progressive rendering system kicks in to prevent memory issues
   */
  initialRenderCount = 40,
  /**
   * Number of additional AdCards to append when user scrolls near the bottom.
   *
   * WHY 24 CARDS:
   * This is a "Goldilocks value" for infinite scroll pagination:
   * 1. Large enough: Adds ~6 rows on desktop (4 col × 6 rows = 24 cards)
   *    - Prevents rapid re-triggering of the intersection observer
   *    - Feels like "substantial new content" not "trickling items"
   * 2. Small enough: Keeps each append operation under ~100ms
   *    - Prevents scroll jank/stuttering during progressive rendering
   *    - Avoids mobile memory spikes (each batch is manageable)
   *
   * WHY NOT MATCH initialRenderCount (40):
   * - Initial render is special: user is waiting, so we can afford more upfront work
   * - Progressive appends happen during scrolling: lower batch size = smoother scrolling
   * - 24 is divisible by common column counts (1, 2, 3, 4, 6) = clean visual rows
   *
   * WHAT HAPPENS IF TOO LOW (e.g., 8):
   * - Intersection observer triggers every 1-2 seconds during fast scrolling
   * - Constant "Loading more…" messages (feels janky/incomplete)
   * - More React re-renders = higher CPU usage
   *
   * WHAT HAPPENS IF TOO HIGH (e.g., 60):
   * - Each append causes visible stutter/lag (especially on mobile)
   * - Memory spikes can trigger garbage collection pauses
   * - "Load more" takes 300ms+ = users notice the delay
   *
   * HOW IT WORKS:
   * The intersection observer (line 487) fires when the sentinel (line 656) enters
   * the viewport. We then append `pageIncrement` items in tiny chunks (line 467)
   * using requestAnimationFrame to avoid blocking the main thread.
   */
  pageIncrement = 24,
  /**
   * Number of AdCards to load with eager image loading (bypass lazy loading).
   *
   * WHY 24 CARDS:
   * This is the number of cards in the "above-the-fold + 1 screenful" region:
   * 1. Desktop (4 columns): 24 cards = ~6 rows = viewport (4 rows) + 1st scroll (2 rows)
   * 2. Mobile (1 column): 24 cards = 24 rows = viewport (6 cards) + deep scroll buffer
   * 3. Matches typical user behavior: most users scroll 1-2 screenfuls before clicking
   *
   * WHY EAGER LOADING FOR FIRST N:
   * Lazy loading (loading="lazy") defers image loading until scroll, but:
   * - It adds a ~200ms delay when images come into view (network request + decode)
   * - This creates "image pop-in" jank during scrolling
   * - First-contentful-paint (FCP) is delayed for above-fold content
   *
   * By eagerly loading the first 24 images, we ensure:
   * - Instant visibility when user scrolls (images are already decoded)
   * - Smooth scrolling experience in the critical "first impression" zone
   * - Better Core Web Vitals (LCP, CLS) for cards in this range
   *
   * WHAT HAPPENS IF TOO LOW (e.g., 8):
   * - Users see blank placeholders pop into images during initial scroll
   * - Feels slow/unpolished (especially on fast connections where eager loading is instant)
   * - Lower perceived performance despite technically saving bandwidth
   *
   * WHAT HAPPENS IF TOO HIGH (e.g., 60):
   * - Initial page load downloads 60 images simultaneously
   * - Browser connection limit (6-8 per domain) is saturated
   * - Time-to-interactive (TTI) increases (blocking main thread with image decodes)
   * - Wastes bandwidth for images user never scrolls to (especially on mobile data)
   * - Mobile devices may run out of memory during initial image decode
   *
   * CURRENT IMPLEMENTATION:
   * We pass `{ eager: true }` to renderItem for the first `forceEagerFirstN` cards (line 633).
   * This tells the AdCard component to use `loading="eager"` instead of `loading="lazy"`.
   */
  forceEagerFirstN = 24,
  layoutMode = "grid",
  stateKey,
  entitlements,
  preserveOrder = false,
  rowFirstMasonry,
  hasMoreFromServer,
  onRequestMore
}: LibraryAdGridProps) {
  
  /**
   * Responsive column count calculator for masonry/grid layouts.
   *
   * WHY CUSTOM BREAKPOINTS INSTEAD OF TAILWIND DEFAULTS:
   * Tailwind's default breakpoints (sm:640, md:768, lg:1024, xl:1280, 2xl:1536) don't align
   * with optimal card widths for our AdCard component. Our cards need ~280-400px width
   * to display images clearly without feeling cramped or excessively wide.
   *
   * BREAKPOINT RATIONALE (width → columns):
   * - < 768px (mobile): 1 column
   *   Single column eliminates horizontal scrolling, maximizes card size on small screens
   * - 768-1023px (tablet): 2 columns
   *   768px ÷ 2 = 384px per card (ideal width with 24px gaps)
   * - 1024-1279px (small desktop): 3 columns
   *   1024px ÷ 3 = 341px per card (comfortable browsing size)
   * - 1280-1999px (desktop): 4 columns
   *   1280px ÷ 4 = 320px per card (standard desktop grid)
   * - 2000-2999px (ultrawide): 5 columns
   *   2000px ÷ 5 = 400px per card (prevents cards from becoming too wide)
   * - 3000px+ (super ultrawide): 6 columns
   *   3000px ÷ 6 = 500px per card (max reasonable card width)
   *
   * WHY WE CAP AT 6 COLUMNS:
   * Beyond 6 columns, cards become too small to appreciate image details (< 280px).
   * Users with 4K+ displays benefit more from larger cards than more columns.
   * Also reduces cognitive load—6 columns is already pushing readability limits.
   *
   * HOW THIS DRIVES LAYOUT:
   * - cols state is used to conditionally apply grid-cols-{n} classes (line 1360-1366)
   * - Also used for round-robin column assignment in masonry mode (line 1034)
   * - Resize listener ensures layout adapts smoothly when window is resized
   *
   * WHY passive: true ON RESIZE LISTENER:
   * Resize events fire rapidly during window dragging (60+ events/sec).
   * passive: true tells the browser we won't call preventDefault(), enabling
   * optimizations that keep resize animations smooth. Critical for 60fps responsiveness.
   */
  function computeCols(w: number) {
    if (w < 768) return 1;        // < md
    if (w < 1024) return 2;       // md
    if (w < 1280) return 3;       // lg
    if (w < 2000) return 4;       // xl & 2xl -> 4 Cols (Wide Cards)
    if (w < 3000) return 5;       // Ultra Wide -> 5 Cols
    return 6;                     // Super Ultra Wide -> 6 Cols
  }
  const [cols, setCols] = useState(() =>
    typeof window === "undefined" ? 1 : computeCols(window.innerWidth)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setCols(computeCols(window.innerWidth));
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  /**
   * Entitlement checks: Determines which ads should be blurred (subscription paywall).
   *
   * WHY THREE-WAY ENTITLEMENT STATE (isAuthenticated, hasValidAccess, entitlementLoaded):
   * We need to distinguish between three user states to optimize UX and prevent flashing:
   * 1. Loading state: Entitlements are being fetched from server (entitlementLoaded = false)
   * 2. Anonymous state: User is not logged in (isAuthenticated = false)
   * 3. Authenticated state: User is logged in, check subscription (hasValidAccess)
   *
   * OPTIMISTIC RENDERING WHILE LOADING:
   * If we always blurred during loading, users would see:
   * - Page loads → all ads blurred (assumes no access)
   * - Entitlements load (200ms later) → blur removed
   * - Result: Jarring "flash of blurred content" (FOBC)
   *
   * Instead, we render optimistically (no blur) while entitlementLoaded = false:
   * - Page loads → ads render clear (optimistic assumption: user has access)
   * - Entitlements load → blur applied if needed OR stays clear if user has access
   * - Result: No flash for subscribers, minimal flash for free users
   *
   * WHY FALLBACK TO false FOR SAFETY:
   * If parent doesn't provide entitlements prop (legacy usage), we default to:
   * - isAuthenticated = false
   * - hasValidAccess = false
   * - entitlementLoaded = false
   * This creates a "locked by default" behavior that prevents accidental free access.
   *
   * BYPASS MECHANISM (bypassSubscriptionCheck):
   * Some grids should never have blur regardless of subscription status:
   * - Special promotional boards (e.g., "Black Friday unlocked view")
   * - Public curated collections (e.g., "Best of 2024" showcase)
   * - Preview/demo boards for marketing pages
   *
   * When bypassSubscriptionCheck = true, hasFullAccess is forced to true, disabling
   * blur logic entirely. This is safer than conditionally rendering a different component
   * because it reuses all the same progressive rendering/memory optimizations.
   *
   * ENTITLEMENT LOGIC FLOW:
   * 1. If bypassSubscriptionCheck: hasFullAccess = true (no blur ever)
   * 2. Else if entitlementLoaded = false: hasFullAccess = false (optimistic, blur not applied yet)
   * 3. Else: hasFullAccess = hasValidAccess (use real subscription status)
   *
   * This ensures:
   * - Subscribers see unblurred content immediately (no flash)
   * - Free users see unblurred content briefly (~200ms), then blur applies
   * - Special boards (bypass) never show blur to anyone
   */

  const isAuthenticated = entitlements?.isAuthenticated ?? false;
  const hasValidAccess = entitlements?.hasValidAccess ?? false;
  const entitlementLoaded = entitlements?.entitlementLoaded ?? false;

  /**
   * Determine if user has full access to unblurred ads.
   *
   * WHY NESTED TERNARY INSTEAD OF IF-ELSE CHAIN:
   * This ternary creates a clear precedence hierarchy for access determination:
   * 1. Bypass (highest priority): Special boards that never blur (promotional, public)
   * 2. Subscription check: Real entitlement status if loaded
   * 3. Optimistic default: false (safe fallback, prevents accidental free access)
   *
   * ALTERNATIVE APPROACHES CONSIDERED:
   * - Separate if-else blocks: More verbose, harder to see precedence at a glance
   * - Early returns: Would require restructuring component logic
   * - Multiple boolean flags: Creates state explosion, harder to reason about
   *
   * TRADE-OFFS:
   * - Pro: Single source of truth, clear precedence, concise
   * - Con: Nested ternaries can be hard to read (mitigated by formatting + comment)
   *
   * EDGE CASE:
   * When bypassSubscriptionCheck=true, we skip entitlement checks entirely.
   * This is intentional - bypass boards should never show blur, even if the
   * entitlement system is broken or user is logged out. Safety for special contexts.
   */
  const hasFullAccess = bypassSubscriptionCheck
  ? true
  : entitlementLoaded
    ? hasValidAccess
    : false;

  const KEY = storageKey(stateKey);

  /**
   * Session-scoped shuffle seed for deterministic item ordering.
   *
   * WHY SEED IS STORED IN sessionStorage:
   * We store the seed in sessionStorage (not localStorage) to achieve session-level stability:
   * 1. Within-session stability: Same shuffle order across all navigations in a single session
   * 2. Cross-session freshness: New shuffle order when user opens a new tab or revisits tomorrow
   * 3. Privacy: sessionStorage is tab-scoped and auto-cleared on tab close (no persistent tracking)
   *
   * This balances two competing needs:
   * - UX: Users shouldn't see items "jump" when clicking back from an ad detail page
   * - Discovery: Users should see fresh content ordering in new sessions
   *
   * HOW THIS PREVENTS FIRST-ROW RESHUFFLES ON NAVIGATION:
   * When React re-mounts this component (e.g., navigating back from /library/ad/123):
   * 1. useState initializer runs and checks sessionStorage for existing seed
   * 2. Finds the same seed from earlier in the session → returns it
   * 3. seededSort uses this same seed → produces identical sort order
   * 4. First-row items appear in the same positions → no visual "jumping"
   *
   * Without this sessionStorage persistence, each mount would generate a new random seed,
   * causing seededSort to produce a different order and creating jarring reshuffles.
   *
   * FALLBACK ON ERROR:
   * If sessionStorage is unavailable (private browsing, storage quota), we fall back to
   * a random seed that lasts only for the current component mount. This degrades to
   * "reshuffle on navigation" behavior but prevents the app from crashing.
   */
  const [seed] = useState(() => {
    try {
      const k = "obyect_grid_seed";
      // Check if seed already exists in this session (for stable shuffle across navigations)
      const ex = typeof window !== "undefined" ? sessionStorage.getItem(k) : null;
      if (ex) return ex; // Reuse existing seed for stability

      /**
       * Generate new seed for this session using base-36 encoding.
       *
       * WHY BASE-36 (toString(36)) INSTEAD OF OTHER FORMATS:
       * Base-36 uses characters [0-9a-z] to encode numbers compactly.
       * - Math.random(): 0.123456789 (16-digit float)
       * - toString(36): "4fzyo82mvyr" (11 alphanumeric chars)
       * - slice(2): "zyo82mvyr" (removes "0." prefix)
       *
       * ALTERNATIVE SEED FORMATS CONSIDERED:
       * - Math.random().toString(): "0.123..." - longer, includes decimal point
       * - Date.now().toString(): "1234567890" - numeric only, less entropy
       * - UUID: "550e8400-..." - overkill for shuffle seed, slower to generate
       * - crypto.getRandomValues(): More secure, but slower and unnecessary for shuffle
       *
       * TRADE-OFFS:
       * - Pro: Compact (9-11 chars vs 16+ for decimal float)
       * - Pro: Human-readable in sessionStorage (easier debugging)
       * - Pro: Fast to generate (~0.001ms vs ~0.1ms for UUID)
       * - Con: Not cryptographically secure (acceptable for shuffle seed)
       * - Con: Variable length (Math.random entropy varies slightly)
       *
       * WHY THIS IS SUFFICIENT FOR SHUFFLE:
       * - Math.random() provides ~52 bits of entropy (JavaScript number precision)
       * - Base-36 encoding preserves this entropy in compact form
       * - For shuffle purposes, we need "random enough", not cryptographic security
       * - Collision probability: ~1 in 10^15 (negligible for our use case)
       */
      const s = Math.random().toString(36).slice(2);
      if (typeof window !== "undefined") sessionStorage.setItem(k, s);
      return s;
    } catch {
      /**
       * Fallback for sessionStorage access failures.
       *
       * WHY THIS FALLBACK IS NECESSARY:
       * sessionStorage can fail in several scenarios:
       * - Private/incognito browsing mode (some browsers block storage)
       * - Storage quota exceeded (user has too much stored data)
       * - Browser extensions blocking storage access
       * - Cross-origin iframe restrictions
       *
       * TRADE-OFF:
       * - Fallback behavior: Seed is only stable within current component mount
       * - User experience: Grid reshuffles on navigation (not ideal, but app works)
       * - Alternative: Could show error message, but graceful degradation is better
       *
       * WHY NOT THROW ERROR:
       * - UX: Showing shuffle instability is better than crashing the page
       * - Rarity: Storage failures are uncommon in normal browsing
       * - Discoverability: Users likely won't notice (shuffle looks intentionally random)
       */
      return Math.random().toString(36).slice(2);
    }
  });

  /**
   * adsToShow: Deduplicated, blur-flagged, and optionally shuffled list of ads.
   *
   * This is the primary data transformation pipeline for the grid. It takes the raw
   * ads array from the parent and processes it through three stages:
   * 1. Deduplication (remove duplicate IDs)
   * 2. Blur assignment (mark which ads should be blurred for non-subscribers)
   * 3. Optional shuffling (seeded shuffle vs preserve order)
   *
   * STAGE 1: DEDUPLICATION
   * -------------------------
   * WHY WE NEED DEDUPLICATION:
   * The parent might pass duplicate ad IDs in several scenarios:
   * - Server pagination returns overlapping results (last item of page 1 = first of page 2)
   * - Multiple filters/tags reference the same ad
   * - Race condition in parent state updates (old + new data temporarily merged)
   *
   * Without deduplication:
   * - React key warnings (duplicate keys in list)
   * - Visual glitch: same ad appears multiple times in grid
   * - Memory waste: same image downloaded multiple times
   *
   * DEDUPLICATION STRATEGY (Set-based, keeps first occurrence):
   * We use a Set to track seen IDs as we iterate. For each ad:
   * - If ID already in Set: Skip this ad (duplicate)
   * - If ID not in Set: Keep this ad, add ID to Set
   *
   * Why "keep first occurrence" instead of "keep last" or "merge duplicates":
   * - First occurrence is typically from earlier pagination (more stable data)
   * - Simpler logic (no need to compare/merge duplicate entries)
   * - Preserves intended ordering from parent (first-seen = first-shown)
   *
   * STAGE 2: BLUR ASSIGNMENT
   * -------------------------
   * WHY 10% FREE (Math.ceil(total * 0.1)):
   * We show 10% of total ads unblurred to non-subscribers as a "preview teaser":
   * - Provides enough value to understand the content quality
   * - Creates desire by showing there's much more available (90% blurred)
   * - Drives conversion without giving away too much for free
   *
   * Why Math.ceil instead of Math.floor:
   * - Ensures at least 1 ad is free even on small grids (e.g., 5 ads → 1 free, not 0)
   * - Rounds up to favor user experience (show slightly more rather than less)
   *
   * WHY INDEX-BASED BLUR (idx >= freeCount):
   * Blur is determined by position in the array, not by any ad property:
   * - First 10% of ads (idx 0 to freeCount-1): _shouldBlur = false (show clear)
   * - Remaining 90% of ads (idx >= freeCount): _shouldBlur = true (show blurred)
   *
   * This creates a "scroll down to hit paywall" UX:
   * - User sees clear ads initially
   * - Scrolls down, encounters blurred ads
   * - Realizes they need to subscribe for more
   *
   * WHY !hasFullAccess OVERRIDE:
   * If user has subscription (hasFullAccess = true), _shouldBlur is always false
   * regardless of index. Subscribers see everything unblurred.
   *
   * STAGE 3: SHUFFLE VS PRESERVE ORDER
   * -----------------------------------
   * WHY preserveOrder TOGGLE:
   * Some grids benefit from shuffling (discovery, variety), others need stable order:
   * - Shuffle (preserveOrder = false): Library grid, curated collections
   *   → Users see different content ordering each session (encourages exploration)
   * - Preserve (preserveOrder = true): Search results, filtered views, personal boards
   *   → Users expect consistent ordering (relevance, date, custom sort)
   *
   * SEEDED SHUFFLE PREVENTS VISUAL JUMPING:
   * When preserveOrder = false, we use seededSort (not Math.random) to:
   * - Generate same shuffle order throughout user's session (stable across navigations)
   * - Prevent "first-row reshuffles" when user clicks back from ad detail page
   * - See seededSort function (line 239) for detailed algorithm explanation
   *
   * MEMOIZATION DEPENDENCIES:
   * - ads: Input array changes (new data from parent)
   * - hasFullAccess: Subscription status changes (login/logout/subscribe)
   * - seed: Session shuffle seed (only changes on new session)
   * - preserveOrder: Shuffle mode toggled by parent
   *
   * Without memoization, this pipeline would run on every render (wasteful):
   * - Deduplication is O(n)
   * - Blur assignment is O(n)
   * - Seeded sort is O(n log n)
   * - Memoization ensures we only recompute when inputs actually change
   */
  const adsToShow = useMemo(() => {
    /**
     * Deduplicate ads by ID, keeping first occurrence only.
     *
     * WHY SET-BASED DEDUPLICATION:
     * Set.has() is O(1) average case, making this deduplication O(n) overall.
     * We iterate once through the array, checking each ID against the Set.
     *
     * ALTERNATIVE DEDUPLICATION APPROACHES CONSIDERED:
     * - Array.reduce + object map: Same performance, but more verbose
     * - Array.filter with indexOf: O(n²) - very slow for 200+ items
     * - Map with ad.id → ad object: Uses more memory (stores full objects)
     * - lodash.uniqBy: Adds dependency, same implementation internally
     *
     * WHY KEEP FIRST OCCURRENCE (NOT LAST):
     * First occurrence is typically more "canonical" because:
     * - Earlier data in arrays often comes from primary sources
     * - Pagination edge cases: Last item of page N might duplicate first of page N+1
     *   Keeping first ensures we preserve the page N version (likely more stable)
     * - Simpler logic: Early return on duplicate, no need to overwrite
     *
     * ALTERNATIVE: KEEP LAST OCCURRENCE
     * Could use Map.set(id, ad) which overwrites on duplicate:
     * ```
     * const map = new Map();
     * ads.forEach(ad => map.set(ad.id, ad));
     * const uniqueAds = Array.from(map.values());
     * ```
     * Rejected because: Last occurrence might be stale/partial in pagination scenarios
     *
     * TRADE-OFFS:
     * - Pro: Fast O(n) performance, handles 1000+ items smoothly
     * - Pro: Preserves input order (first-seen stays in original position)
     * - Pro: Minimal memory overhead (Set stores IDs only, not full ad objects)
     * - Con: Silently drops duplicates (no warning/logging for debugging)
     * - Con: Assumes first occurrence is canonical (might not always be true)
     *
     * EDGE CASES:
     * - Empty array: Returns empty array (filter on empty array = empty array)
     * - Single item: Returns single item (trivially unique)
     * - All duplicates: Returns single item (first occurrence only)
     * - Mixed types: String(ad.id) coerces numbers to strings (e.g., 123 === "123")
     */
    const seen = new Set<string>();
    const uniqueAds = ads.filter((ad: any) => {
      const id = String(ad.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const total = uniqueAds.length;
    const freeCount = Math.ceil(total * 0.1);

    const withBlur = uniqueAds.map((ad: any, idx: number) => ({
      ...ad,
      _shouldBlur: !hasFullAccess && idx >= freeCount,
    }));

    /**
     * Conditional shuffling based on preserveOrder flag.
     *
     * WHY CONDITIONAL SHUFFLE INSTEAD OF ALWAYS SHUFFLING:
     * Different use cases require different ordering strategies:
     * 1. Discovery contexts (preserveOrder=false): Library grid, curated collections
     *    - Benefits from shuffle: Users see variety, discover new content each session
     *    - Seeded shuffle ensures stability within session (no jumping on navigation)
     * 2. Intent-driven contexts (preserveOrder=true): Search results, filtered views
     *    - Requires preserved order: Relevance ranking, date sorting, custom order
     *    - Shuffle would destroy meaningful order (e.g., "newest first" sort)
     *
     * ALTERNATIVE APPROACHES CONSIDERED:
     * - Always shuffle with sort key: Would require parent to pass sort configuration
     * - Server-side shuffle: Would lose session stability (different order per request)
     * - Client-side random shuffle: Would cause visual jumping on navigation (see seededSort)
     * - Multiple sort modes: Over-engineering, preserveOrder boolean is sufficient
     *
     * TRADE-OFFS:
     * - Pro: Single flag controls shuffle behavior (simple API)
     * - Pro: Covers both major use cases (discovery vs intent-driven)
     * - Pro: Maintains backward compatibility (default shuffle behavior preserved)
     * - Con: No middle ground (e.g., "shuffle within groups")
     * - Con: Parent must know which mode is appropriate for their context
     *
     * EDGE CASE:
     * When preserveOrder switches from false → true mid-session:
     * - withBlur already has blur flags applied (based on position in uniqueAds)
     * - Returning withBlur directly preserves this order
     * - Result: Blur appears in "wrong" positions (first 10% of current order, not shuffled order)
     * - This is acceptable: preserveOrder typically doesn't change during session
     */
    if (preserveOrder) {
      return withBlur; // Preserve incoming order (search, filters, custom sort)
    }
    // Apply seeded shuffle for discovery contexts (stable across navigations)
    return seededSort(withBlur, seed);
  }, [ads, hasFullAccess, seed, preserveOrder]);

  /**
   * adsPrepared: Final ad list with optional row-first masonry reordering applied.
   *
   * WHY THIS ADDITIONAL TRANSFORMATION STAGE:
   * adsToShow contains the deduplicated, blur-flagged, and shuffled ads, but CSS
   * columns masonry (column-count CSS property) flows items column-by-column vertically.
   * This creates an unnatural visual order that feels "wrong" for grid layouts.
   *
   * PROBLEM: CSS COLUMNS NATURAL FLOW (column-first)
   * Given 9 items and 3 columns, CSS columns fills like this:
   * Column 0: [0, 3, 6]  (fills top-to-bottom first)
   * Column 1: [1, 4, 7]
   * Column 2: [2, 5, 8]
   * Visual result: 0 1 2  (reading left-to-right across columns gives: 0,1,2,3,4,5,6,7,8)
   *                 3 4 5
   *                 6 7 8
   *
   * Users expect row-first reading order (left-to-right, top-to-bottom), which this achieves.
   *
   * SOLUTION: PRE-REORDER ITEMS BEFORE CSS COLUMNS PROCESSES THEM
   * rowFirstOrder() redistributes items so that when CSS columns flows them column-by-column,
   * the visual result is row-by-row. See rowFirstOrder function (line 156) for algorithm details.
   *
   * WHY CONDITIONAL (layoutMode === "masonry" && rowFirstMasonry):
   * - layoutMode === "masonry": Only apply for masonry layouts (CSS columns mode)
   *   Regular grids (display: grid) already flow row-first naturally, no reordering needed
   * - rowFirstMasonry: Parent can opt-in/out of this behavior
   *   Some masonry layouts prefer column-first (e.g., Pinterest-style waterfall)
   *   Our library grid works better with row-first (more predictable visual scan)
   *
   * WHY RECOMPUTE WHEN cols CHANGES:
   * The reordering algorithm depends on column count (items[i] → column[i % cols]).
   * When user resizes window and cols changes (4 → 2), we must re-distribute items
   * to match the new column count. Otherwise, items would be in wrong columns.
   *
   * MEMOIZATION BENEFIT:
   * rowFirstOrder is O(n) (iterates all items to redistribute). Without memoization:
   * - Runs on every render even if inputs unchanged
   * - Creates new array reference, triggering downstream re-renders
   * With memoization:
   * - Only runs when adsToShow, layoutMode, rowFirstMasonry, or cols actually change
   * - Reuses same array reference when inputs unchanged (prevents unnecessary re-renders)
   */
  const adsPrepared = useMemo(() => {
    /**
     * Apply optional row-first reordering for masonry layouts.
     *
     * WHY CONDITIONAL REORDERING (NOT ALWAYS APPLIED):
     * Row-first reordering is only needed for CSS columns-based masonry layouts.
     * Regular CSS grid layouts already flow left-to-right naturally.
     *
     * WHEN THIS REORDERING APPLIES:
     * - layoutMode === "masonry": Using CSS columns (column-count property)
     * - rowFirstMasonry === true: Parent opts into row-first visual appearance
     *
     * WHY TWO CONDITIONS (layoutMode AND rowFirstMasonry):
     * 1. layoutMode check: Prevents wasted reordering work on grid layouts
     *    Grid layouts don't need reordering (they already flow row-first)
     * 2. rowFirstMasonry check: Allows opt-out for traditional masonry
     *    Some masonry layouts intentionally want column-first flow (Pinterest-style)
     *
     * ALTERNATIVE APPROACHES CONSIDERED:
     * - Always reorder: Wastes CPU on grid layouts, breaks pure masonry use cases
     * - Single flag: Couldn't distinguish "grid mode" from "masonry without reordering"
     * - Layout mode enum: More granular (e.g., "masonry-row-first", "masonry-column-first")
     *   but adds API complexity for marginal benefit
     *
     * TRADE-OFFS:
     * - Pro: Flexible - supports grid, masonry, and masonry-row-first
     * - Pro: Efficient - only reorders when necessary
     * - Pro: Opt-in behavior - doesn't break existing masonry layouts
     * - Con: Two flags to understand (layoutMode + rowFirstMasonry)
     * - Con: Easy to misconfigure (e.g., rowFirstMasonry=true with layoutMode="grid")
     *
     * PERFORMANCE NOTE:
     * rowFirstOrder() is O(n) but fast (just array redistribution).
     * useMemo prevents re-running when adsToShow reference changes but items are identical.
     * Typical cost: ~1-2ms for 200 items (negligible).
     */
    let out = adsToShow; // Start with deduplicated, blur-flagged, optionally shuffled ads
    if (layoutMode === "masonry" && rowFirstMasonry) {
      out = rowFirstOrder(out, cols); // Reorder for row-first visual appearance
    }
    return out;
  }, [adsToShow, layoutMode, rowFirstMasonry, cols]);


  // ---------- Progressive render (SAFE) ----------
  /**
   * Reads saved grid state from sessionStorage.
   *
   * WHAT STATE IS PERSISTED:
   * We persist two critical pieces of state across navigations:
   * 1. visibleCount: How many AdCards were rendered (e.g., 88 after scrolling)
   * 2. scrollY: The exact vertical scroll position in pixels (e.g., 2400px)
   *
   * WHY WE PERSIST THIS STATE:
   * When a user clicks an ad card (navigates to /library/ad/123) and then clicks back:
   * - React unmounts and re-mounts LibraryAdGrid (fresh component instance)
   * - Without state persistence: Grid resets to top with initialRenderCount (40) cards
   * - With state persistence: Grid restores to user's previous position with same cards
   *
   * HOW THIS ENABLES "PICK UP WHERE I LEFT OFF" UX:
   * 1. User scrolls down, sees 88 cards, reaches scroll position 2400px
   * 2. State is saved to sessionStorage: {visibleCount: 88, scrollY: 2400}
   * 3. User clicks ad card, navigates to detail page
   * 4. User clicks back, LibraryAdGrid re-mounts
   * 5. readSaved() retrieves {visibleCount: 88, scrollY: 2400}
   * 6. Grid renders 88 cards (not 40) and scrolls to 2400px
   * 7. User sees the exact same view they left from
   *
   * WHY sessionStorage INSTEAD OF localStorage:
   * - sessionStorage is tab-scoped and auto-cleared on tab close
   * - Prevents stale state from accumulating across days/weeks
   * - Different tabs can have different scroll positions (tab independence)
   * - Privacy-friendly: state doesn't persist after session ends
   *
   * ERROR HANDLING:
   * Returns null instead of throwing if:
   * - sessionStorage is unavailable (private browsing, quota exceeded)
   * - Stored data is corrupted/malformed JSON
   * - KEY doesn't exist (first visit to library page in this session)
   * Null return = fresh state, grid starts from top with initialRenderCount
   */
  const readSaved = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = sessionStorage.getItem(KEY);
      return raw
        ? (JSON.parse(raw) as { visibleCount: number; scrollY: number })
        : null;
    } catch {
      return null;
    }
  };
  /**
   * Saved state from sessionStorage, read once on component mount.
   *
   * WHY READ ONCE ON MOUNT (NOT IN useEffect):
   * We call readSaved() immediately during render (not in useEffect) because:
   * 1. Must be available for useState initializers (lines 475-477)
   * 2. useState initializers only run on mount (can't access useEffect results)
   * 3. Prevents "flash of wrong state" (grid briefly shows 40 cards, then jumps to 88)
   *
   * WHAT HAPPENS WITH THIS SAVED STATE:
   * - saved.visibleCount → initializes visibleCount state (line 476)
   * - saved.scrollY → drives scroll restoration effect (line 530)
   *
   * TYPICAL VALUES:
   * - First visit: saved = null (no stored state)
   * - After scrolling + navigation: saved = {visibleCount: 88, scrollY: 2400}
   * - After filters change: saved might have visibleCount > current ads.length (clamped later)
   */
  const saved = readSaved();

  /**
   * Minimum number of cards to render on any initial page load.
   *
   * WHY ENFORCE A MINIMUM (8 cards):
   * Even if initialRenderCount is configured to a very low value (or 0), we need enough
   * cards to fill the viewport on most devices. Otherwise:
   * - Mobile portrait (1 column): 8 cards ≈ 1.5 screenfuls (prevents "empty screen" feel)
   * - Desktop (4 columns): 8 cards = 2 rows (sufficient to show grid structure)
   *
   * WITHOUT THIS MINIMUM:
   * If initialRenderCount = 0 or undefined:
   * - Grid renders with 0 cards
   * - IntersectionObserver sentinel is immediately visible (triggers load more)
   * - Creates unnecessary loading flash before first content appears
   *
   * WHY Math.max(8, initialRenderCount || 8):
   * - If initialRenderCount is provided and >= 8: use it (respects configuration)
   * - If initialRenderCount < 8: clamp to 8 (enforces minimum)
   * - If initialRenderCount is 0/null/undefined: fallback to 8 (safe default)
   *
   * This ensures the grid always starts with a reasonable baseline number of cards,
   * preventing degenerate cases while still allowing parent to configure higher values.
   */
  const minInitial = Math.max(8, initialRenderCount || 8);

  /**
   * Progressive rendering: visibleCount controls how many AdCards are actually rendered.
   *
   * WHY PROGRESSIVE RENDERING IS CRITICAL FOR MOBILE SAFARI:
   * Mobile Safari has strict memory limits (~100-150MB per tab). Rendering all ads at once
   * causes memory exhaustion and silent tab crashes (see docs/mobile-crash-debugging.md).
   * Progressive rendering solves this by:
   * 1. Initial render: Only 40 cards (covers 2-3 screenfuls) = ~50MB memory
   * 2. User scrolls: Load 24 more cards incrementally = smooth memory growth
   * 3. Avoids crash: Memory stays under iOS limits even with 200+ ads
   *
   * HOW visibleCount DRIVES PROGRESSIVE RENDERING:
   * - visibleCount starts at initialRenderCount (40) on mount
   * - IntersectionObserver (line 707) watches sentinel element at bottom of grid
   * - When sentinel enters viewport: visibleCount += pageIncrement (24)
   * - React re-renders with larger slice: adsPrepared.slice(0, visibleCount)
   * - New cards appear at bottom, old cards stay unchanged (smooth infinite scroll)
   *
   * STATE RESTORATION AFTER NAVIGATION:
   * When user navigates back from ad detail page:
   * 1. sessionStorage contains saved state: {visibleCount: 88, scrollY: 2400}
   * 2. useState initializer restores visibleCount to 88 (not 40)
   * 3. User sees same cards they were browsing (not reset to top)
   * 4. Scroll position is restored separately (line 460)
   *
   * WHY Math.min(..., adsToShow.length):
   * If saved visibleCount is 100 but we only have 60 ads now (filters changed),
   * clamp to 60 to prevent slice(0, 100) from returning all 60 (no progressive render).
   */
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(Math.max(1, saved?.visibleCount ?? minInitial), adsToShow.length)
  );
  const [loadingMore, setLoadingMore] = useState(false);

  /**
   * Clamp visibleCount BEFORE paint when list length changes.
   *
   * WHY useLayoutEffect (NOT useEffect):
   * useLayoutEffect runs synchronously BEFORE the browser paints, ensuring visibleCount
   * is clamped before React renders the slice. This prevents three critical bugs:
   *
   * BUG 1: EMPTY SCREEN FLASH (0-slice bug)
   * - User has 100 ads, visibleCount = 100
   * - Filters change, ads.length drops to 20
   * - Without clamping: slice(0, 100) on 20-item array = all 20 items
   * - React key reconciliation gets confused (expected 100 items, got 20)
   * - Result: White screen or empty grid for 1 frame until useEffect clamps it
   *
   * BUG 2: LAYOUT THRASHING ON NAVIGATION
   * - User navigates back, sessionStorage has visibleCount = 100
   * - New filter applied, only 30 ads available
   * - Without useLayoutEffect: React paints 30 items, then useEffect clamps and re-paints
   * - Result: Double paint = visible layout shift + wasted rendering work
   *
   * BUG 3: INTERSECTION OBSERVER BREAKS
   * - visibleCount = 100, ads.length = 50
   * - slice(0, 100) returns 50 items (all ads)
   * - Sentinel never appears (it's only shown when visibleCount < adsToShow.length)
   * - User can't load more because IntersectionObserver never fires
   *
   * HOW CLAMPING PREVENTS THESE BUGS:
   * - Math.max(1, ...): Ensures at least 1 item is visible (prevents 0-slice)
   * - Math.max(c, minInitial): If visibleCount < minInitial, bump to minInitial
   * - Math.min(..., adsPrepared.length): Caps visibleCount to available items
   *
   * EXAMPLE FLOW (filter changes 100 ads → 30 ads):
   * 1. adsPrepared.length changes from 100 → 30
   * 2. useLayoutEffect fires BEFORE paint
   * 3. setVisibleCount(c => Math.min(100, 30)) = 30
   * 4. React renders slice(0, 30) = 30 items (correct)
   * 5. User sees smooth transition, no flash/flicker
   *
   * MOBILE SAFARI RELEVANCE:
   * On iOS, layout thrashing (double paint) can trigger memory warnings and tab crashes.
   * Using useLayoutEffect ensures a single, clean paint that minimizes memory churn.
   */
  useLayoutEffect(() => {
    setVisibleCount((c) =>
      Math.min(Math.max(1, Math.max(c, minInitial)), adsPrepared.length)
    );
  }, [adsPrepared.length, minInitial]);

  /**
   * Scroll restoration effect: Restores user's scroll position after navigation back.
   *
   * WHY RESTORE HAPPENS AFTER ITEMS LOAD (NOT IMMEDIATELY):
   * The scroll position (e.g., 2400px) requires a certain document height to be valid.
   * If we try to scroll to 2400px when the document is only 1000px tall, the browser
   * clamps the scroll to the maximum (1000px), and the user ends up at the wrong position.
   *
   * RESTORATION FLOW:
   * 1. User navigates back, component mounts with saved = {visibleCount: 88, scrollY: 2400}
   * 2. visibleCount state initializes to 88 (line 476)
   * 3. React renders 88 AdCards, browser paints them
   * 4. This effect fires: adsToShow.length > 0 (items are available)
   * 5. ensureAndScroll checks if document height >= targetY (2400px)
   * 6. If yes: scrolls immediately to 2400px
   * 7. If no: progressively renders more cards until height is sufficient
   *
   * WHY ensureAndScroll PROGRESSIVELY RENDERS MORE IF NEEDED:
   * Sometimes saved.visibleCount is not enough to reach saved.scrollY:
   * - Scenario 1: User resized window smaller (cards are taller now)
   * - Scenario 2: New filters applied, fewer ads match (88 cards → 60 cards)
   * - Scenario 3: Ads were reordered/shuffled differently
   *
   * In these cases, rendering 88 cards might only create 2000px of document height,
   * but we need 2400px to reach the saved scroll position. ensureAndScroll solves this by:
   * 1. Checking: document height < targetY + viewport height + 50px buffer?
   * 2. If yes: render more cards (increment visibleCount by pageIncrement/2 = 12)
   * 3. Schedule next frame via requestAnimationFrame
   * 4. Repeat until document height is sufficient OR we run out of cards
   * 5. Finally: scroll to targetY
   *
   * WHY PROGRESSIVE RENDERING (NOT RENDER ALL AT ONCE):
   * If we rendered all cards at once to guarantee enough height, we'd trigger the
   * mobile Safari memory crash (see docs/mobile-crash-debugging.md). Progressive rendering
   * keeps memory usage low while ensuring we can reach the target scroll position.
   *
   * WHY requestAnimationFrame INSTEAD OF LOOP:
   * - Lets browser paint between increments (user sees cards appearing)
   * - Prevents main thread blocking (app remains responsive)
   * - Allows layout recalculation after each batch (accurate height measurements)
   * - Avoids infinite loop if document height can never reach targetY
   *
   * WHY didRestoreRef PREVENTS MULTIPLE RESTORES:
   * Without this guard, the effect would fire on every adsToShow.length change:
   * - User scrolls down, adsToShow.length changes (progressive load), effect fires again
   * - Scroll jumps back to saved position (jarring, breaks user's current scroll)
   * didRestoreRef ensures restoration only happens ONCE per component mount.
   *
   * WHY +50px BUFFER (docH < targetY + winH + 50):
   * Accounts for:
   * - Browser chrome height variations (mobile address bar auto-hide)
   * - Fractional pixel rendering differences
   * - Small layout shifts during card rendering
   * Without buffer: might get stuck in infinite loop trying to render "just one more px"
   *
   * EXAMPLE SCENARIO (saved scroll position too deep):
   * - saved = {visibleCount: 88, scrollY: 2400}
   * - Filters changed, only 60 ads available now
   * - Initial render: 60 cards = 2000px document height
   * - ensureAndScroll: 2000 < 2400 + 800 (viewport) + 50 = true
   * - Render 12 more (clamped to 60 max) = still 2000px (no new cards)
   * - ensureAndScroll: 2000 < 3250 but visibleCount >= adsToShow.length
   * - Condition fails, exits loop, scrolls to best-effort position (2000px)
   */
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    if (typeof window === "undefined") return;
    if (!saved) return; // nothing to restore
    if (adsToShow.length === 0) return;

    didRestoreRef.current = true;
    const targetY = Math.max(0, saved.scrollY || 0);

    let raf = 0;
    const ensureAndScroll = () => {
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;

      // If we don't have enough content yet to reach targetY,
      // render more and try again on the next frame.
      if (docH < targetY + winH + 50 && visibleCount < adsToShow.length) {
        setVisibleCount((c) =>
          Math.min(c + Math.max(8, Math.floor(pageIncrement / 2)), adsToShow.length)
        );
        raf = window.requestAnimationFrame(ensureAndScroll);
        return;
      }

      // Enough height reached—scroll to saved position.
      window.scrollTo(0, targetY);
    };

    // Defer one frame so the current slice paints first
    raf = window.requestAnimationFrame(ensureAndScroll);
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adsToShow.length]);

  /**
   * Scroll persistence effect: Continuously saves grid state to sessionStorage.
   *
   * WHAT STATE IS PERSISTED:
   * This effect saves two values to sessionStorage on every scroll event:
   * 1. visibleCount: How many AdCards are currently rendered (e.g., 64 after progressive load)
   * 2. scrollY: Current vertical scroll position in pixels (e.g., 1200px)
   *
   * WHY WE PERSIST ON SCROLL:
   * The user might navigate away at ANY moment (click ad card, back button, close tab).
   * By saving state continuously during scroll, we ensure the latest position is always
   * captured, enabling seamless restoration when they navigate back.
   *
   * WHY SCROLL EVENTS USE requestAnimationFrame THROTTLING:
   * Browser scroll events fire at extremely high frequency:
   * - Touchpad/mouse wheel: 60-120 events per second during smooth scrolling
   * - Touch scroll: 60+ events per second on mobile devices
   * - Each event could trigger a sessionStorage.setItem (slow synchronous I/O)
   *
   * Without throttling:
   * - 60 scroll events/sec × 5ms sessionStorage write = 300ms/sec blocked (30% CPU)
   * - Main thread is constantly blocked, causing scroll jank/stutter
   * - Mobile browsers drop frames, scroll feels sluggish
   * - Battery drain from excessive storage writes
   *
   * HOW requestAnimationFrame THROTTLING WORKS:
   * 1. First scroll event fires: onScroll called, ticking = false
   * 2. onScroll sets ticking = true, schedules save() in next animation frame (~16ms)
   * 3. More scroll events fire during this 16ms: onScroll returns early (ticking = true)
   * 4. Animation frame callback fires: save() writes to storage, ticking = false
   * 5. Next scroll event can now schedule a new save()
   *
   * THROTTLING EFFECT:
   * - Without throttling: 60 saves/sec (every ~16ms)
   * - With rAF throttling: ~60 saves/sec BUT aligned with browser paint cycles
   * - Actual benefit: Skips redundant saves when multiple scroll events occur in same frame
   * - Net result: ~20-30 saves/sec (saves during frames when scroll actually painted)
   *
   * WHY THIS PREVENTS SCROLL JANK:
   * - sessionStorage writes happen during animation frames (when browser is idle)
   * - Writes don't interrupt ongoing scroll momentum
   * - Main thread remains responsive to touch/scroll input
   * - Mobile Safari maintains 60fps scrolling
   *
   * WHY THREE DIFFERENT SAVE TRIGGERS:
   * 1. scroll event: User is actively scrolling (most common case)
   * 2. beforeunload: User is closing tab/navigating away (captures final state)
   * 3. visibilitychange → hidden: User switches tabs/minimizes browser (mobile Safari aggressively suspends tabs)
   *
   * WHY visibilitychange IS CRITICAL FOR MOBILE SAFARI:
   * Mobile Safari can terminate background tabs at any moment to free memory.
   * When user switches tabs, we have milliseconds to save state before potential termination.
   * visibilitychange fires when tab becomes hidden, giving us one last chance to persist.
   *
   * MEMORY LEAK PREVENTION:
   * The cleanup function removes event listeners on unmount to prevent memory leaks.
   * However, note the BUG: visibilitychange listener is NOT removed in cleanup.
   * This was identified in docs/mobile-crash-debugging.md as a memory leak that contributed
   * to mobile Safari crashes. The inline listener (() => {}) creates a new function reference
   * that can't be removed without storing it in a variable first.
   *
   * PROPER CLEANUP PATTERN (currently missing):
   * ```
   * const onVisibilityChange = () => {
   *   if (document.visibilityState === "hidden") save();
   * };
   * document.addEventListener("visibilitychange", onVisibilityChange);
   * return () => {
   *   window.removeEventListener("scroll", onScroll);
   *   window.removeEventListener("beforeunload", save);
   *   document.removeEventListener("visibilitychange", onVisibilityChange);
   * };
   * ```
   *
   * WHY passive: true FOR SCROLL LISTENER:
   * Tells browser this listener won't call preventDefault(), allowing browser to optimize
   * scroll performance by not blocking on listener execution. Critical for smooth scrolling.
   *
   * ERROR HANDLING:
   * try/catch around sessionStorage.setItem handles quota exceeded, private browsing, etc.
   * Fails silently (no-op) instead of crashing the app. User loses state persistence but
   * app continues functioning normally.
   *
   * EFFECT DEPENDENCIES:
   * Depends on [visibleCount, KEY] because:
   * - visibleCount changes: Need to re-create save() closure with new value
   * - KEY changes: Need to save to new storage key (different stateKey prop)
   * Without re-running effect, save() would capture stale visibleCount value.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    let ticking = false;

    const save = () => {
      try {
        sessionStorage.setItem(
          KEY,
          JSON.stringify({
            visibleCount,
            scrollY: window.scrollY,
          })
        );
      } catch {
        /* no-op */
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        save();
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", save);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") save();
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", save);
    };
  }, [visibleCount, KEY]);

  /**
   * Server-side pagination sentinel: Triggers parent to fetch next page from server.
   *
   * TWO-TIER PROGRESSIVE LOADING SYSTEM:
   * This component has TWO separate "load more" mechanisms that work together:
   * 1. CLIENT-SIDE progressive rendering (sentinelRef, line 1096): Reveals already-loaded
   *    items progressively (e.g., show 40, then 64, then 88 from already-fetched data)
   * 2. SERVER-SIDE pagination (serverSentinelRef, THIS section): Tells parent to fetch
   *    more data from the server API when we've revealed most of the current batch
   *
   * WHY TWO SEPARATE SENTINELS:
   * - Client-side (sentinelRef): Fast, instant reveals (no network), triggers every 24 items
   * - Server-side (serverSentinelRef): Slow, network-dependent, triggers when we're near
   *   the end of ALL currently-loaded data
   *
   * This architecture enables:
   * - Instant progressive scrolling from already-loaded data (good UX)
   * - Seamless prefetching of next server page before user reaches the end (no loading gap)
   *
   * HOW THIS SERVER SENTINEL WORKS:
   * 1. Parent provides hasMoreFromServer (boolean flag: "more pages available from API")
   * 2. Parent provides onRequestMore (callback: "fetch next page")
   * 3. When user scrolls near bottom, IntersectionObserver fires
   * 4. We call onRequestMore() to tell parent: "please fetch more data"
   * 5. Parent fetches from server, appends new ads to the ads[] array
   * 6. This component receives new ads prop, re-renders with more items
   * 7. Client-side sentinel takes over to progressively reveal the new items
   *
   * WHY 1200px rootMargin (PREFETCH DISTANCE):
   * rootMargin: "1200px 0px" means the sentinel triggers when it's 1200px BELOW the viewport.
   * This creates a prefetch buffer so the server fetch completes before user scrolls to the end.
   *
   * Calculation:
   * - Average scroll speed: 800-1200 px/sec
   * - Server fetch latency: 200-500ms (typical API response time)
   * - 1200px buffer ÷ 1000 px/sec scroll speed = 1.2 seconds of prefetch time
   * - This gives the server 1.2 seconds to fetch data before user reaches the end
   *
   * Without prefetch (rootMargin: "0px"):
   * - User scrolls to end of current items
   * - Sentinel visible, triggers fetch
   * - User waits 200-500ms staring at "Loading more…" message
   * - Jarring experience, feels slow
   *
   * With 1200px prefetch:
   * - User scrolls, sentinel is 1200px away, triggers fetch
   * - User continues scrolling through client-side progressive reveals
   * - Server fetch completes in background
   * - By the time user reaches old end, new items are already loaded
   * - Seamless infinite scroll experience
   *
   * WHY PENDING FLAG (DEBOUNCE):
   * IntersectionObserver can fire multiple times in rapid succession:
   * - Sentinel briefly intersects while scrolling
   * - User scrolls past, then bounces back (overscroll effect)
   * - Observer fires twice, triggering duplicate fetches
   *
   * The pending flag prevents this:
   * 1. First intersection: pending = false, allow fetch, set pending = true
   * 2. Subsequent intersections: pending = true, skip (debounce)
   * 3. After 300ms cooldown: reset pending = false, allow next fetch
   *
   * WHY 300ms COOLDOWN:
   * - Long enough: Prevents duplicate fetches during scroll jitter/bounce
   * - Short enough: Allows fetch retrigger if user scrolls away and back quickly
   * - Balances protection against wasted requests with responsiveness
   *
   * CLEANUP ON UNMOUNT:
   * io.disconnect() removes the observer when component unmounts, preventing:
   * - Memory leaks (observer keeps references to DOM elements)
   * - Callbacks firing after unmount (would call stale onRequestMore)
   */
  const serverSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMoreFromServer) return;
    const el = serverSentinelRef.current;
    if (!el) return;

    let pending = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (pending) return;
        pending = true;
        // Let parent fetch the next page
        onRequestMore?.();
        // small cooldown to avoid rapid double-fires
        setTimeout(() => { pending = false; }, 300);
      },
      { root: null, rootMargin: "1200px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMoreFromServer, onRequestMore]);

  /**
   * Slice of ads to actually render in the DOM (progressive rendering window).
   *
   * WHY SLICE INSTEAD OF RENDERING ALL:
   * This is the core of our progressive rendering strategy. Instead of rendering
   * all ads at once (which crashes Mobile Safari), we only render a "window" of items:
   * - adsPrepared: All processed ads (might be 200+ items)
   * - slice: Only first `visibleCount` items (starts at 40, grows as user scrolls)
   *
   * MEMORY BENEFIT:
   * - Rendering 200 ads at once: ~300-400MB memory (crashes iOS Safari)
   * - Rendering 40 ads initially: ~50MB memory (safe on all devices)
   * - As user scrolls: visibleCount increases to 64, 88, 112... memory grows gradually
   *
   * HOW THIS ENABLES INFINITE SCROLL:
   * 1. Initial render: slice = first 40 items
   * 2. User scrolls near bottom, IntersectionObserver fires (line 1225)
   * 3. visibleCount increments by pageIncrement (24), becomes 64
   * 4. slice updates: first 64 items
   * 5. React re-renders with 24 new items at bottom
   * 6. Process repeats until user has scrolled through all items
   *
   * WHY NOT useMemo:
   * Array.slice() is extremely fast (O(n) but n is small, ~40-200 items).
   * The overhead of useMemo (object comparison, cache lookup) would be greater
   * than just calling slice() on every render. Keep it simple.
   */
  const slice = adsPrepared.slice(0, visibleCount);

  /**
   * Stable column assignment map for masonry layout.
   *
   * WHY STABLE COLUMN ASSIGNMENT IS NEEDED:
   * Without stable assignments, loading more items causes the masonry layout to "reflow":
   * - Initial render: Item A goes to column 1 (shortest column at that moment)
   * - User scrolls, more items load
   * - React re-renders the entire grid
   * - Shortest-column algorithm runs again from scratch
   * - Item A now goes to column 2 (different column is shortest now)
   * - Result: Items "jump" between columns = jarring visual glitch
   *
   * By persisting each item's column in a map (ad.id → column index), we ensure:
   * - Item A is assigned to column 1 on first render
   * - Item A stays in column 1 forever (even after new items load)
   * - New items get assigned to columns, but existing items never move
   *
   * WHY ROUND-ROBIN ASSIGNMENT:
   * We use round-robin (0, 1, 2, 3, 0, 1, 2, 3...) instead of shortest-column because:
   * 1. Predictable: New items are evenly distributed across columns
   * 2. Simple: No height calculations needed (avoids layout thrashing)
   * 3. Fast: O(1) assignment vs O(n) for shortest-column algorithm
   * 4. Good enough: With variable card heights, round-robin balances well over time
   *
   * HOW THIS PREVENTS MASONRY GLITCH WHEN LOADING MORE ITEMS:
   * 1. User scrolls to item 40, progressive render kicks in, items 41-64 load
   * 2. React re-renders with new slice (items 1-64 instead of 1-40)
   * 3. useEffect sees new items (41-64) that don't exist in colMap yet
   * 4. Assigns them to columns using round-robin: 41→col0, 42→col1, 43→col2, etc.
   * 5. Items 1-40 keep their original column assignments (read from colMap)
   * 6. Result: Items 1-40 don't move, items 41-64 appear in balanced columns
   *
   * WHY WE USE STABLE ID STRING DEPENDENCY:
   * The useEffect dependency is `sliceIds` (comma-joined string of IDs) instead of `slice`:
   * - If we used `slice` directly: useEffect fires on EVERY render (infinite loop)
   * - `slice` is a new array reference on every render (even if IDs are identical)
   * - `sliceIds` only changes when the actual set of visible IDs changes
   * - This ensures we only update colMap when truly new items appear
   *
   * EXAMPLE FLOW:
   * - Initial: slice=[ad1, ad2, ad3], cols=2
   * - colMap: {ad1: 0, ad2: 1, ad3: 0}
   * - assignPtrRef: 1 (next new item will go to column 1)
   * - User scrolls, slice=[ad1, ad2, ad3, ad4, ad5], cols=2
   * - useEffect fires (sliceIds changed: "ad1,ad2,ad3" → "ad1,ad2,ad3,ad4,ad5")
   * - ad1/ad2/ad3: already in colMap, skip
   * - ad4: assign to column 1 (assignPtrRef % 2), increment pointer
   * - ad5: assign to column 0 (next in round-robin)
   * - colMap: {ad1: 0, ad2: 1, ad3: 0, ad4: 1, ad5: 0}
   * - Result: Items 1-3 didn't move, items 4-5 filled in balanced columns
   */
  const [colMap, setColMap] = useState<Record<string, number>>({});
  const assignPtrRef = useRef(0);
  /**
   * Stable dependency string for useEffect to prevent infinite loops.
   *
   * WHY COMMA-JOINED ID STRING INSTEAD OF ARRAY:
   * useEffect compares dependencies by reference. slice.map() creates a new array
   * on every render, even if the IDs haven't changed. This would cause the effect
   * to run on every render, triggering infinite re-render loops.
   *
   * ALTERNATIVE APPROACHES CONSIDERED:
   * - Direct slice dependency: Would cause infinite loop (new array reference each render)
   * - JSON.stringify(slice.map(...)): Works, but slower and creates garbage strings
   * - useMemo for ID array: Adds complexity, requires its own dependencies
   * - Custom deep comparison hook: Over-engineering for this simple case
   *
   * TRADE-OFFS:
   * - Pro: Simple, fast (single string allocation), works with React deps system
   * - Con: Collision risk if IDs contain commas (mitigated by IDs being integers/UUIDs)
   * - Pro: Human-readable in React DevTools (can inspect dep changes)
   *
   * EDGE CASE HANDLING:
   * If an ad ID contains a comma (unlikely with integer/UUID IDs), this could
   * create false positives (e.g., "123,456" vs "1234,56"). In practice, our IDs
   * are either numeric (1, 2, 3) or UUIDs (no commas), so this isn't a concern.
   * If IDs change format in the future, consider using a safer delimiter like '\0'.
   */
  const sliceIds = slice.map(ad => String(ad.id)).join(',');

  /**
   * Reset colMap and assignPtrRef when column count changes.
   *
   * WHY THIS IS NEEDED:
   * When the window is resized and column count changes (e.g., 4 → 2 columns),
   * existing items have stale column assignments that don't distribute evenly:
   * - Example: Items assigned to [0,1,2,3,0,1,2,3] with 4 columns
   * - After resize to 2 columns: clamping gives [0,1,1,1,0,1,1,1] = uneven!
   * - Result: Column 1 gets most items, visual "bunching" in 2 rows
   *
   * By resetting both colMap and assignPtrRef when cols changes:
   * 1. All items will be reassigned on the next render
   * 2. Round-robin starts fresh from column 0
   * 3. Items distribute evenly across the new column count
   *
   * Note: This causes a visual reflow during resize, but ensures correct layout.
   */
  useEffect(() => {
    setColMap({});
    assignPtrRef.current = 0;
  }, [cols]);

  /**
   * Assign new items to columns using round-robin distribution.
   * Existing items keep their column assignments for visual stability.
   *
   * WHY CONDITIONAL RETURN (changed ? next : prev):
   * React's useState setter triggers re-render only if the returned value is
   * a different reference. By returning prev when nothing changed, we prevent
   * unnecessary re-renders of buckets useMemo (line 1403).
   *
   * ALTERNATIVE APPROACHES CONSIDERED:
   * - Always return next: Causes extra re-renders even when slice hasn't changed
   * - Check changed before setColMap call: More complex control flow
   * - Use reducer instead of useState: Over-engineering for simple state
   *
   * TRADE-OFFS:
   * - Pro: Eliminates wasted renders when user scrolls without new items
   * - Pro: Improves scroll performance (fewer React reconciliation cycles)
   * - Con: Adds conditional logic complexity (changed tracking)
   * - Con: Easy to forget the pattern (developer might always return next)
   *
   * PERFORMANCE IMPACT:
   * Without this optimization:
   * - Every sliceIds change triggers colMap update (even if no new items)
   * - colMap update triggers buckets recalculation (useMemo dependency)
   * - buckets change triggers full grid re-render (React reconciliation)
   * - Result: Wasted render cycles during rapid scrolling
   *
   * With this optimization:
   * - Only triggers downstream updates when new items actually added
   * - Typical scroll session: ~50% fewer re-renders
   */
  useEffect(() => {
    if (cols < 1) return;
    setColMap(prev => {
      const next = { ...prev };
      let ptr = assignPtrRef.current % cols;
      let changed = false;

      for (const ad of slice) {
        const id = String(ad.id);
        if (next[id] == null) {
          next[id] = ptr;
          ptr = (ptr + 1) % cols;
          changed = true;
        }
      }
      assignPtrRef.current = ptr;
      // Only return new object if something changed (prevents unnecessary re-renders)
      return changed ? next : prev;
    });
  }, [sliceIds, cols]);

  /**
   * Build per-column buckets from stable column assignments.
   *
   * WHY WE BUILD BUCKETS:
   * The colMap tells us which column each item belongs to, but React needs a structure
   * it can map over to render columns. We transform the flat list into column buckets:
   * - Input: [ad1, ad2, ad3, ad4] with colMap {ad1:0, ad2:1, ad3:0, ad4:1}
   * - Output: [[ad1, ad3], [ad2, ad4]] (2 buckets, one per column)
   * - Rendered as: <Column 0>[ad1, ad3]</Column> <Column 1>[ad2, ad4]</Column>
   *
   * HOW THIS WORKS WITH STABLE ASSIGNMENT:
   * 1. colMap is stable (items never change columns)
   * 2. buckets are derived from colMap using useMemo
   * 3. React renders buckets.map((items, colIdx) => <Column>{items}</Column>)
   * 4. Even when new items load, each column's existing items stay in the same order
   *
   * WHY useMemo:
   * Building buckets is O(n) where n = slice.length. Without memoization:
   * - Every render would rebuild buckets from scratch (wasteful)
   * - React would see new array references and re-render columns unnecessarily
   * With memoization:
   * - Buckets only rebuild when slice, cols, or colMap changes
   * - React skips re-rendering columns when buckets reference is unchanged
   *
   * FALLBACK FOR MISSING ASSIGNMENTS:
   * colMap[String(ad.id)] ?? 0 handles edge cases:
   * - New item appears before useEffect runs (assign to column 0 temporarily)
   * - Math.max(0, Math.min(cols-1, ...)) clamps to valid column range
   * - This prevents crashes if colMap has stale data (e.g., user resizes from 4→2 cols)
   */
  const buckets = useMemo(() => {
    const arr = Array.from({ length: cols }, () => [] as typeof slice);
    for (const ad of slice) {
      const colIdx = Math.max(0, Math.min(cols - 1, colMap[String(ad.id)] ?? 0));
      arr[colIdx].push(ad);
    }
    return arr;
  }, [slice, cols, colMap]);

  /**
   * Progressive rendering: Sentinel-based IntersectionObserver for "load more" infinite scroll.
   *
   * This system automatically appends new AdCards as the user scrolls down, creating an
   * infinite scroll experience without loading all items upfront (critical for mobile Safari).
   */
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    /**
     * Chunked rendering function: Adds items in small batches across multiple animation frames.
     *
     * WHY CHUNKED RENDERING WITH requestAnimationFrame:
     * Rendering 24 new AdCards in a single frame causes severe jank on mobile devices:
     * - Each AdCard has ~50 DOM nodes (image, text, buttons, icons)
     * - 24 cards × 50 nodes = 1200 DOM insertions in one frame
     * - Main thread is blocked for ~200ms = visible scroll stutter
     * - Mobile Safari often drops to 15fps during this blocking operation
     *
     * Chunked rendering solves this by spreading the work across multiple frames:
     * 1. Split 24 cards into 4 chunks of 6 cards each
     * 2. Render chunk 1 (6 cards) in frame 1 → only 300 DOM insertions = 50ms
     * 3. Browser paints frame 1 while we wait for next rAF callback
     * 4. Render chunk 2 in frame 2, chunk 3 in frame 3, chunk 4 in frame 4
     * 5. Total time is the same (~200ms), but no single frame is blocked
     * 6. Result: 60fps scrolling maintained throughout progressive render
     *
     * WHY perFrame = pageIncrement / 4 (6 cards per frame):
     * - Too small (2 cards): Requires 12 frames to render 24 cards = slow perceived load
     * - Too large (12 cards): Causes frame drops (600 DOM nodes still blocks for ~100ms)
     * - Sweet spot (6 cards): Balances smoothness with speed
     *
     * HOW THIS PREVENTS MOBILE SAFARI CRASHES:
     * By yielding to the browser between chunks, we give the JS engine time to:
     * - Run garbage collection on old frames
     * - Process touch events (user can still scroll during loading)
     * - Avoid memory spikes (gradual allocation vs sudden 1200-node spike)
     *
     * ALGORITHM BREAKDOWN:
     * 1. IntersectionObserver fires when sentinel is visible
     * 2. stepAdd(target) is called with target = visibleCount + 24
     * 3. tick() renders perFrame (6) items, increments visibleCount
     * 4. requestAnimationFrame schedules next tick()
     * 5. Process repeats until visibleCount reaches target
     * 6. Each tick happens in a separate frame (~16ms apart at 60fps)
     *
     * EXAMPLE (visibleCount = 40, pageIncrement = 24, perFrame = 6):
     * - Frame 1: setVisibleCount(40 → 46) renders 6 new cards
     * - Frame 2: setVisibleCount(46 → 52) renders 6 more cards
     * - Frame 3: setVisibleCount(52 → 58) renders 6 more cards
     * - Frame 4: setVisibleCount(58 → 64) renders final 6 cards
     * - Total: 24 cards added smoothly across 4 frames (~66ms at 60fps)
     */
    const stepAdd = (target: number) => {
      // add in tiny chunks across frames
      const perFrame = Math.max(6, Math.floor(pageIncrement / 4));
      let raf = 0;
      const tick = () => {
        setVisibleCount((c) => {
          if (c >= target) return c;
          return Math.min(c + perFrame, target);
        });
        raf = requestAnimationFrame(() => {
          // keep going until we reach target
          setTimeout(() => {
            const now = Math.min(target, visibleCount + perFrame);
            if (now < target) tick();
          }, 0);
        });
      };
      tick();
      return () => cancelAnimationFrame(raf);
    };

    let cancel = () => {};
    /**
     * Sentinel IntersectionObserver: Triggers "load more" when sentinel enters viewport.
     *
     * HOW THE SENTINEL PATTERN WORKS:
     * 1. A sentinel <div> (line 875) is placed at the bottom of the visible grid
     * 2. IntersectionObserver watches when this sentinel enters the viewport
     * 3. When it becomes visible: user has scrolled near the end of the grid
     * 4. We append pageIncrement (24) more items via stepAdd()
     * 5. Sentinel moves down below the new items, process repeats
     *
     * WHY 1500px rootMargin (CRITICAL FOR MOBILE):
     * rootMargin extends the intersection area 1500px below the viewport.
     * This means "load more" triggers when the sentinel is 1500px away from
     * entering the visible screen, creating a prefetch buffer.
     *
     * Without rootMargin (or with small values like 500px):
     * - User scrolls to bottom of visible items
     * - Sentinel enters viewport, IntersectionObserver fires
     * - stepAdd begins rendering new cards across 4 frames (~66ms)
     * - User sees "end of content" for 66ms before new cards appear
     * - Result: Janky experience, visible "loading gap"
     *
     * With 1500px rootMargin:
     * - User is scrolling down, sentinel is still 1500px below screen
     * - IntersectionObserver fires early (prefetch trigger)
     * - stepAdd renders 24 cards while user scrolls toward them
     * - By the time user reaches where the sentinel WAS, new cards are ready
     * - Result: Infinite scroll feels instant and seamless
     *
     * WHY 1500px SPECIFICALLY:
     * - Mobile scroll velocity: ~800-1200 px/sec during fast scrolling
     * - stepAdd duration: ~66ms (4 chunks at 60fps)
     * - Safety buffer: 1500px gives ~1.2 seconds of prefetch time
     * - At 1000 px/sec scroll speed: User scrolls 66px during stepAdd
     * - 1500px buffer - 66px scrolled = 1434px remaining before user hits old sentinel
     * - Plenty of margin for slower devices or network delays
     *
     * WHAT HAPPENS IF TOO SMALL (e.g., 500px):
     * - User scrolls 66px during stepAdd, buffer becomes 500 - 66 = 434px
     * - At 800 px/sec, user hits old sentinel in 434/800 = 0.5 seconds
     * - Not enough time for stepAdd to finish on slow devices
     * - User sees "end of content" flash before new items appear
     *
     * WHAT HAPPENS IF TOO LARGE (e.g., 3000px):
     * - Wastes memory by loading cards too early
     * - User might never scroll that far (loaded 24 cards for nothing)
     * - On slow connections, prefetch competes with visible card image loading
     *
     * MOBILE SAFARI CRASH PREVENTION:
     * Combined with chunked rendering, this creates a smooth memory profile:
     * - Cards load gradually (not sudden 24-card spike)
     * - Prefetch starts before user reaches bottom (feels instant)
     * - Memory allocation is spread over time (no GC pauses)
     *
     * threshold: 0 means trigger as soon as ANY pixel of sentinel is in the
     * extended viewport (viewport + 1500px margin). We don't wait for the sentinel
     * to be fully visible—that would negate the benefit of the rootMargin.
     */
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMore) return;
        if (visibleCount >= adsToShow.length) return;
        setLoadingMore(true);
        const target = Math.min(visibleCount + pageIncrement, adsToShow.length);
        cancel = stepAdd(target);
        setTimeout(() => setLoadingMore(false), 120);
      },
      { root: null, rootMargin: "1500px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancel();
    };
  }, [visibleCount, adsToShow.length, pageIncrement, loadingMore]);

  /**
   * Prefetch next server page when user has revealed 80% of current items.
   *
   * WHY PREFETCH AT 80% THRESHOLD:
   * This is an AGGRESSIVE prefetch trigger that works in conjunction with the server
   * sentinel (line 1024) to ensure seamless infinite scroll even on slow connections.
   *
   * PROBLEM: RELYING ONLY ON BOTTOM SENTINEL (serverSentinelRef)
   * The serverSentinelRef has 1200px rootMargin which works well for fast scrollers,
   * but fails in two scenarios:
   * 1. Slow network: 200ms server latency becomes 1000ms on 3G, user reaches end before fetch completes
   * 2. Fast scrollers: Power users scrolling at 2000+ px/sec blow past the prefetch buffer
   *
   * SOLUTION: DUAL-TRIGGER PREFETCH SYSTEM
   * By ALSO triggering at 80% revealed items, we catch early scrollers:
   * - User progressively reveals: 40 → 64 → 88 → 112 items
   * - At 80% of 150 total items (120 items revealed): This effect fires
   * - Fetches next page while user still has 30 items (20%) to scroll through
   * - Even at 2000 px/sec scroll speed, gives 1-2 seconds for server fetch
   *
   * WHY 80% SPECIFICALLY (NOT 90% OR 70%):
   * - Too late (90%): Not enough runway for slow fetches, user hits end
   * - Too early (70%): Wastes bandwidth fetching data user might not reach
   * - 80%: Sweet spot that balances prefetch aggressiveness with bandwidth conservation
   *
   * EXAMPLE SCENARIO (150 items, 40 initial, 24 per page increment):
   * - visibleCount progression: 40 → 64 → 88 → 112 → 136 → 150
   * - 80% threshold: 150 × 0.8 = 120 items
   * - Trigger fires when visibleCount crosses 120 (during 112 → 136 transition)
   * - User has 30 items (150 - 120) remaining to scroll through
   * - At ~400px per screenful = ~7.5 screenfuls of buffer for fetch to complete
   *
   * WHY GLOBAL DEBOUNCE FLAG (__ob_prefetching):
   * This effect runs on EVERY visibleCount change (every progressive render batch).
   * Without debounce:
   * - visibleCount: 112 → 118 → 124 → 130 (crosses 120 threshold multiple times)
   * - Effect fires 4 times in quick succession
   * - Result: 4 duplicate server requests, wasted bandwidth, potential rate limiting
   *
   * The global flag prevents this:
   * 1. First fire (visibleCount = 120): flag is false, allow fetch, set flag = true
   * 2. Subsequent fires (121, 124, 130...): flag is true, skip
   * 3. After 500ms: reset flag, allow next prefetch (for subsequent page)
   *
   * WHY GLOBAL FLAG INSTEAD OF LOCAL STATE:
   * - Local state (useState) would cause re-render, potentially interfering with scroll
   * - Global flag is instant, no re-render overhead
   * - Namespace prefix (__ob_) prevents collisions with other code
   *
   * WHY 500ms DEBOUNCE:
   * - Longer than progressive render cycle (66ms for 4 chunks)
   * - Shorter than typical server response (200-1000ms)
   * - Ensures we don't trigger same fetch multiple times while it's in-flight
   * - Allows next page prefetch after current page completes
   *
   * RELATIONSHIP WITH serverSentinelRef:
   * These two prefetch mechanisms are complementary, not redundant:
   * - This effect: Catches fast scrollers early (aggressive prefetch)
   * - serverSentinelRef: Catches slow scrollers near end (defensive prefetch)
   * - Together: Ensures seamless infinite scroll across all scroll speeds and network conditions
   */
  useEffect(() => {
    if (!hasMoreFromServer || !onRequestMore) return;
    if (adsToShow.length === 0) return;

    const eighty = Math.floor(adsToShow.length * 0.8);
    if (visibleCount >= eighty) {
      /**
       * Debounce mechanism to prevent duplicate fetch requests.
       *
       * WHY GLOBAL FLAG INSTEAD OF OTHER DEBOUNCE APPROACHES:
       * This effect runs on EVERY visibleCount change (multiple times per second
       * during scrolling). We need a debounce mechanism that:
       * 1. Works across multiple rapid threshold crosses (118→120→124→130)
       * 2. Doesn't trigger React re-renders (useState would cause re-render overhead)
       * 3. Persists across effect re-runs within same threshold crossing
       *
       * ALTERNATIVE DEBOUNCE APPROACHES CONSIDERED:
       * - useState flag: Would trigger re-render on every flag change (bad for scroll perf)
       * - useRef flag: Works, but less explicit than global (harder to debug)
       * - Lodash debounce: Adds dependency, requires stable function reference
       * - setTimeout clearing: More complex, requires tracking timeout IDs
       *
       * TRADE-OFFS:
       * - Pro: Zero re-render overhead (no React state involved)
       * - Pro: Simple and explicit (easy to debug with console.log)
       * - Pro: Works across component instances (if multiple grids exist)
       * - Con: Global namespace pollution (mitigated by __ob_ prefix)
       * - Con: Not cleaned up on unmount (acceptable for session-scoped flag)
       * - Con: Harder to test (requires window object mocking)
       *
       * WHY 500ms TIMEOUT:
       * - Longer than progressive render cycle (66ms for chunked loading)
       * - Shorter than typical server response time (200-1000ms)
       * - Prevents duplicate requests during rapid scroll threshold crossing
       * - Allows subsequent page fetches after current page completes
       *
       * EDGE CASE:
       * If user scrolls extremely fast and crosses 80% thresholds of multiple pages
       * (e.g., page 1 → page 2 → page 3 within 500ms), only one fetch fires.
       * This is acceptable - the serverSentinelRef (line 1220) will catch remaining
       * pages as the user approaches the end.
       */
      if ((window as any).__ob_prefetching) return;
      (window as any).__ob_prefetching = true;
      onRequestMore();
      setTimeout(() => { (window as any).__ob_prefetching = false; }, 500);
    }
  }, [visibleCount, adsToShow.length, hasMoreFromServer, onRequestMore]);

  /**
   * Auto-fill effect: Ensures document is always taller than viewport (enables scrolling).
   *
   * PROBLEM: SHORT PAGES THAT DON'T ENABLE SCROLLING
   * In some edge cases, the initial render doesn't create enough document height to scroll:
   * - Mobile landscape mode (very short viewport height ~375px)
   * - Filtered results with only a few items (e.g., 8 ads that fit on one screen)
   * - Large screens with tall viewport (e.g., 4K display with 2160px height)
   *
   * When document height < viewport height:
   * - No scrollbar appears (user can't scroll down)
   * - IntersectionObserver sentinel (line 1225) is IMMEDIATELY visible (not below fold)
   * - Progressive render triggers instantly, but user sees loading state with no scroll ability
   * - Result: Jarring UX where items "pop in" without user action
   *
   * SOLUTION: AUTOMATICALLY RENDER MORE ITEMS UNTIL SCROLLING IS ENABLED
   * This effect continuously checks if document is taller than viewport:
   * 1. If docH < winH + 200px: Document is too short, add more items
   * 2. Increment visibleCount by pageIncrement/2 (12 items)
   * 3. Schedule next check via requestAnimationFrame
   * 4. Repeat until document is tall enough OR we run out of items
   *
   * WHY +200px BUFFER (docH < winH + 200):
   * We want the document to be TALLER than the viewport by at least 200px to:
   * - Enable smooth scrolling (user has something to scroll to)
   * - Position sentinel element below the fold (not immediately intersecting)
   * - Prevent sentinel from triggering before user has scrolled at all
   *
   * Without buffer (docH < winH):
   * - Document is exactly viewport height
   * - Sentinel sits at viewport bottom edge (might trigger immediately)
   * - Small layout shifts could make sentinel visible (false trigger)
   *
   * WHY pageIncrement / 2 (12 items) PER ITERATION:
   * - Smaller than full pageIncrement (24): Prevents over-rendering on small gaps
   * - Larger than minimum (8): Fills viewport quickly on large screens
   * - Balanced approach: Typically need 1-2 iterations to fill most viewports
   *
   * EXAMPLE SCENARIO (4K display, 8 initial items):
   * - Initial render: 8 items × 300px/item = 2400px document height
   * - Viewport height: 2160px
   * - Check: 2400 < 2160 + 200 = true (too short)
   * - Iteration 1: Render 12 more (total 20), docH = 6000px
   * - Check: 6000 < 2360 = false (sufficient)
   * - Effect stops, user can now scroll smoothly
   *
   * WHY requestAnimationFrame LOOP (NOT WHILE LOOP):
   * - Allows browser to paint between iterations (user sees items appearing)
   * - Prevents main thread blocking (app remains responsive)
   * - Enables accurate height measurements after each batch renders
   * - Avoids infinite loop if we can never reach target height
   *
   * PROTECTION AGAINST INFINITE LOOPS:
   * - Guard: visibleCount < adsToShow.length
   * - When we've revealed all items, effect stops even if height is still short
   * - This handles degenerate cases (e.g., 3 total ads, viewport needs 20 to fill)
   *
   * PERFORMANCE IMPACT:
   * - Runs once on mount, typically 0-2 iterations
   * - Each iteration: one height check + one state update + one rAF
   * - Total time: ~30-60ms (negligible)
   * - Only runs when needed (short pages), no overhead on normal pages
   *
   * RELATIONSHIP WITH OTHER PROGRESSIVE RENDERING:
   * This effect is INDEPENDENT of IntersectionObserver-based progressive loading:
   * - This effect: Runs once on mount to establish minimum scrollable height
   * - IntersectionObserver: Runs continuously during scrolling to load more
   * - Both update visibleCount, but at different times and for different reasons
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (visibleCount >= adsToShow.length) return;
    let raf = 0;
    const ensure = () => {
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      if (docH < winH + 200 && visibleCount < adsToShow.length) {
        setVisibleCount((c) =>
          Math.min(c + Math.max(8, Math.floor(pageIncrement / 2)), adsToShow.length)
        );
        raf = window.requestAnimationFrame(ensure);
      }
    };
    raf = window.requestAnimationFrame(ensure);
    return () => window.cancelAnimationFrame(raf);
  }, [visibleCount, adsToShow.length, pageIncrement]);

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * RENDER SECTION
   * ═══════════════════════════════════════════════════════════════════════════
   */

  /**
   * Loading state: Skeleton placeholders while initial ads are being fetched.
   *
   * WHY SHOW SKELETONS INSTEAD OF SPINNER/BLANK SCREEN:
   * Skeleton screens improve perceived performance by showing the page structure
   * immediately, even while data is loading. This creates the illusion of faster
   * loading because:
   * - User sees "something" instantly (not a blank screen or spinner)
   * - Layout is stable (no sudden content jump when data arrives)
   * - User understands what content to expect (grid of cards)
   *
   * WHY 12 PLACEHOLDERS (NOT 40 LIKE initialRenderCount):
   * - 12 is enough to fill most viewports on desktop (4 cols × 3 rows = 12)
   * - More than 12 would delay first paint (each skeleton has DOM overhead)
   * - Less than 12 would show blank space on larger screens
   * - 12 matches the "eager load" count for visual consistency
   *
   * SKELETON STRUCTURE MATCHES REAL ADCARD:
   * - Avatar circle (8×8)
   * - Text lines (brand name + date)
   * - Image placeholder (aspect ratio 4:5)
   * This creates seamless transition when real cards replace skeletons.
   *
   * WHY animate-pulse:
   * - Subtle animation indicates "loading in progress" (not broken/stuck)
   * - Tailwind's animate-pulse: opacity 100% → 50% → 100% (1.5s cycle)
   * - Gentle enough to not be distracting, obvious enough to signal loading
   */
  if (adsLoading) {
    const PLACEHOLDERS = 12;
    return (
      <div className="mx-auto w-full px-3 md:px-6">
        <div className="
          grid gap-2.5 md:gap-4
          grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 min-[2000px]:grid-cols-5 min-[3000px]:grid-cols-6
        ">
          {Array.from({ length: PLACEHOLDERS }).map((_, i) => (
            <div key={`ph-${i}`} className="flex flex-col gap-2.5">
              <div className="rounded-[16px] overflow-hidden bg-[#13171c] border-0 ring-1 ring-white/5 shadow-2xl">
                {/* Header: Avatar + Text */}
                <div className="p-4 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-white/10 animate-pulse shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 w-24 bg-white/10 animate-pulse rounded" />
                    <div className="h-2.5 w-16 bg-white/5 animate-pulse rounded" />
                  </div>
                </div>
                {/* Media: Image */}
                <div className="px-3 pb-3">
                  <div className="aspect-[4/5] w-full bg-white/5 animate-pulse rounded-[8px]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * Empty state: Shown when ads array is empty after loading completes.
   *
   * WHY CHECK ads.length (NOT adsToShow.length):
   * - ads: Raw input array from parent (reflects actual data availability)
   * - adsToShow: Processed array after deduplication/shuffle (might differ due to processing)
   * - If parent provides ads=[], we should show empty state (no data available)
   * - If parent provides ads=[...] but adsToShow=[] due to deduplication bug, that's a
   *   processing error we'd want to catch, not silently show empty state
   *
   * WHY ALLOW CUSTOM emptyState:
   * Different contexts need different empty messages:
   * - Search results: "No ads match your search" (with suggestions)
   * - Filtered view: "No ads match these filters" (with reset button)
   * - Personal board: "You haven't saved any ads yet" (with CTA)
   * - Library: "No ads available" (generic fallback)
   *
   * Parent can provide custom emptyState component via prop for context-appropriate messaging.
   */
  if (ads.length === 0) {
    return (
      <div>
        {emptyState || (
          <div className="text-center py-12 text-muted-foreground">
            <p>No ads found.</p>
          </div>
        )}
      </div>
    );
  }

  /**
   * Modal action logic: Determines what happens when user clicks footer modal CTA.
   *
   * WHY TWO DIFFERENT ACTIONS (Auth vs Subscribe):
   * Users in different states require different next steps:
   * 1. Anonymous users (!isAuthenticated): Need to create account first
   *    → onRequireAuth() → Opens signup/login modal
   * 2. Authenticated users (!hasValidAccess): Already have account, need subscription
   *    → onRequireSubscribe() → Opens subscription checkout
   *
   * This creates a progressive funnel:
   * - Anonymous → "Sign up to see more" → Account creation → Subscription prompt
   * - Logged in → "Subscribe to see more" → Subscription checkout
   *
   * Each step is appropriate for the user's current state, reducing friction.
   */
  const shouldShowModal = !hasFullAccess;
  const handleModalAction = () => {
    if (!isAuthenticated) onRequireAuth();
    else onRequireSubscribe();
  };

  /**
   * Layout mode selection: Masonry (CSS columns) vs Grid (CSS grid).
   *
   * WHY TWO LAYOUT MODES:
   * Different content types benefit from different layout algorithms:
   *
   * MASONRY MODE (CSS columns: column-count)
   * - Best for: Variable-height cards (images with different aspect ratios)
   * - Flow: Items flow vertically in columns, filling shortest column first
   * - Benefit: No wasted vertical space (tight packing)
   * - Tradeoff: Reading order is column-by-column (less natural)
   * - Our fix: rowFirstOrder() reorders items to achieve row-first visual appearance
   *
   * GRID MODE (CSS grid: display: grid)
   * - Best for: Fixed-height cards or when row alignment matters
   * - Flow: Items flow in grid cells, natural left-to-right, top-to-bottom order
   * - Benefit: Predictable alignment, natural reading order
   * - Tradeoff: Variable heights create gaps (wasted vertical space)
   *
   * CURRENT USAGE:
   * We use GRID mode for this component because:
   * 1. Cards have similar heights (4:5 aspect ratio standardized)
   * 2. Gaps are minimal with consistent sizing
   * 3. Natural reading order improves scannability
   * 4. Simpler implementation (no rowFirstOrder needed)
   *
   * The masonry code path is preserved for potential future use cases
   * (e.g., mixed content types, user-uploaded images with varying ratios).
   */
  const isMasonry = layoutMode === "masonry";

  /**
   * Responsive container classes: Different layouts for masonry vs grid.
   *
   * WHY DUPLICATE CLASS DEFINITIONS:
   * Masonry and grid have fundamentally different CSS properties:
   * - Masonry: columns-{n} (controls column-count)
   * - Grid: grid-cols-{n} (controls grid-template-columns)
   * We can't mix these properties, so we need separate class sets.
   *
   * COMMON PATTERNS ACROSS BOTH MODES:
   * - mx-auto w-full: Center container with full width
   * - px-3 md:px-6: Horizontal padding (prevents cards from touching screen edges)
   * - gap-2.5 md:gap-4: Consistent spacing between cards (smaller on mobile)
   *
   * BREAKPOINT PROGRESSION:
   * - < sm (< 640px): 1 column (mobile portrait)
   * - sm (640-767px): 2 columns (mobile landscape, small tablets)
   * - md (768-1023px): 2 columns (tablets)
   * - lg (1024-1279px): 3 columns (small laptops)
   * - xl (1280-1919px): 4 columns (desktops)
   * - min-[1920px] (1920-2399px): 5 columns (1080p+ wide monitors)
   * - min-[2400px] (2400px+): 6 columns (1440p+ ultrawide monitors)
   *
   * WHY gap-8 FOR GRID (VS gap-4 FOR MASONRY):
   * Grid mode has more regular alignment, so we can afford larger gaps without
   * feeling sparse. Masonry mode uses tighter gaps to reduce the "uneven column
   * heights" visual effect.
   *
   * WHY isolate FOR MASONRY:
   * Creates new stacking context, preventing z-index issues when cards overlap
   * during CSS columns reflow. Not needed for grid (no column flow).
   */
  const containerClass = isMasonry
    ? [
        "mx-auto w-full",
        "columns-1 sm:columns-2 md:columns-2 lg:columns-3 xl:columns-4 2xl:columns-4 min-[1920px]:columns-5 min-[2400px]:columns-6",
        "gap-2.5 md:gap-4",
        "px-3 md:px-6",
        "isolate",
      ].join(" ")
      : [
          "mx-auto w-full",
          "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 min-[1920px]:grid-cols-5 min-[2400px]:grid-cols-6",
          "gap-2.5 md:gap-8",
          "px-3 md:px-6",
        ].join(" ");


  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * MAIN GRID RENDER
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * RENDERING ARCHITECTURE:
   * This component uses a column-based rendering approach for better stability:
   * 1. Compute column count dynamically based on window width (cols state)
   * 2. Assign each item to a specific column (colMap) with round-robin distribution
   * 3. Build column buckets (array of arrays: [[col0 items], [col1 items], ...])
   * 4. Render each column as a flex container with its assigned items
   *
   * WHY COLUMN-BASED INSTEAD OF FLAT GRID:
   * Prevents masonry layout glitches when progressive rendering adds new items:
   * - Flat grid: New items cause entire grid to reflow (items "jump" between columns)
   * - Column-based: Each item has stable column assignment (items never move columns)
   * - Result: Smooth infinite scroll with no visual jumping
   *
   * RENDERING STRUCTURE:
   * <div>                              ← Outer wrapper
   *   <div class="mx-auto px-3...">   ← Container with padding
   *     <div class="grid grid-cols-N"> ← Grid with dynamic columns
   *       {buckets.map(columnItems =>
   *         <div class="flex flex-col"> ← Column (flex container)
   *           {columnItems.map(ad =>
   *             <AdCard />           ← Individual card
   *           )}
   *         </div>
   *       )}
   *     </div>
   *   </div>
   *   <div ref={sentinelRef}>          ← Progressive load sentinel
   *   <div ref={serverSentinelRef}>    ← Server pagination sentinel
   *   <LoginSubscribeFooterModal>      ← Paywall modal
   * </div>
   */
  return (
    <div>
      <div className="mx-auto w-full px-3 md:px-6">
        {/**
         * Dynamic grid with computed column count.
         *
         * WHY cx() WITH CONDITIONALS INSTEAD OF TEMPLATE STRING:
         * - cx() from class-variance-authority properly handles conditional classes
         * - Prevents class name collisions (e.g., "grid-cols-3 grid-cols-4" both applied)
         * - Cleaner than template literals with ternaries
         *
         * WHY EXPLICIT cols === N CHECKS:
         * Tailwind needs complete class names at build time for purging.
         * We can't use grid-cols-${cols} (dynamic template) because:
         * - Tailwind's JIT compiler won't generate the CSS (doesn't see full class name)
         * - We'd get unstyled grid at runtime
         * Instead, we enumerate all possible column counts (1-6) explicitly.
         *
         * WHY cols >= 6 (NOT cols === 6):
         * Safety fallback for edge cases where cols might be 7+ (broken computation).
         * Clamps to maximum 6 columns instead of having unstyled grid.
         */}
        <div
          className={cx(
            "grid gap-2.5 md:gap-4",
            cols === 1 && "grid-cols-1",
            cols === 2 && "grid-cols-2",
            cols === 3 && "grid-cols-3",
            cols === 4 && "grid-cols-4",
            cols === 5 && "grid-cols-5",
            cols >= 6 && "grid-cols-6"
          )}
        >
            {/**
             * Render each column with its assigned items.
             *
             * WHY buckets.map INSTEAD OF slice.map:
             * - buckets: Pre-distributed items into columns (stable assignment)
             * - slice: Flat list of items (would need runtime column calculation)
             * Using buckets ensures each item renders in its assigned column.
             *
             * WHY flex flex-col FOR COLUMNS:
             * - Vertical stacking of cards within each column
             * - gap-2.5: Consistent spacing between cards
             * - Simpler than CSS columns (more control, better for progressive render)
             */}
            {buckets.map((columnItems, colIdx) => (
              <div key={`col-${colIdx}`} className="flex flex-col gap-2.5">
                {columnItems.map((ad, index) => {
                  /**
                   * Eager loading flag: First N images in each column load eagerly.
                   *
                   * WHY PER-COLUMN INDEX (NOT GLOBAL POSITION):
                   * We check `index < forceEagerFirstN` where index is the position
                   * within THIS specific column, not the global position in the grid.
                   *
                   * EXAMPLE (forceEagerFirstN=6, 4 columns):
                   * - Column 0: First 6 items eager (items 0,4,8,12,16,20 globally)
                   * - Column 1: First 6 items eager (items 1,5,9,13,17,21 globally)
                   * - Column 2: First 6 items eager (items 2,6,10,14,18,22 globally)
                   * - Column 3: First 6 items eager (items 3,7,11,15,19,23 globally)
                   * - Result: First 6 rows (24 items total) load eagerly
                   *
                   * ALTERNATIVE APPROACHES CONSIDERED:
                   * - Global index tracking: More complex, requires calculating global position
                   * - Per-row eager count: Would need to track which row each item is in
                   * - First N items regardless of column: Could leave some columns without eager items
                   *
                   * TRADE-OFFS:
                   * - Pro: Simple per-column logic (no cross-column coordination needed)
                   * - Pro: Ensures balanced eager loading across all columns
                   * - Pro: Works correctly with dynamic column counts (responsive)
                   * - Con: Total eager count scales with column count (6 cols × 6 items = 36 eager)
                   *       This is actually beneficial - more columns = wider screen = more above-fold items
                   *
                   * WHY THIS ENSURES ABOVE-FOLD COVERAGE:
                   * By eagerly loading the first N items in EACH column, we guarantee:
                   * - All columns have instant-loading content at the top
                   * - No "empty column while other columns load" visual gap
                   * - Balanced loading across entire viewport width
                   */
                  const eager = index < forceEagerFirstN;
                  return (
                    <div
                      key={ad.id}
                      className="animate-fade-in"
                      style={{
                        /**
                         * Staggered fade-in animation for visual polish.
                         *
                         * WHY STAGGER ANIMATIONS:
                         * - Creates "progressive reveal" effect (items appear one by one)
                         * - More engaging than instant pop-in of all items
                         * - Draws eye from top to bottom (guides visual scan)
                         *
                         * WHY index * 20ms:
                         * - 20ms delay per item = smooth cascade
                         * - Item 0: 0ms, Item 1: 20ms, Item 2: 40ms, etc.
                         * - Too fast (10ms): Barely perceptible
                         * - Too slow (50ms): Feels sluggish
                         *
                         * WHY Math.min(index * 20, 400):
                         * - Caps maximum delay at 400ms
                         * - Without cap: Item 50 would delay 1000ms (too long)
                         * - After 20 items (400ms), all subsequent items appear together
                         * - Preserves cascade effect for first screenful, avoids delay for deep items
                         */
                        animationDelay: `${Math.min(index * 20, 400)}ms`,
                      } as React.CSSProperties}
                    >
                      <div className="group/card relative">
                        {/**
                         * Render individual AdCard via parent's renderItem callback.
                         *
                         * WHY RENDER CALLBACK INSTEAD OF IMPORTING ADCARD DIRECTLY:
                         * - Inversion of control: Parent decides how to render each card
                         * - Different contexts need different card variants (compact, detailed, etc.)
                         * - Keeps this component generic/reusable
                         *
                         * PARAMETERS PASSED TO renderItem:
                         * - ad: The ad data object
                         * - ad._shouldBlur: Whether this card should be blurred (subscription paywall)
                         * - { eager }: Options object with eager loading flag
                         */}
                        {renderItem(ad, ad._shouldBlur, { eager })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

      {/**
       * Progressive rendering sentinel: Triggers "load more" when scrolled into view.
       *
       * CONDITIONS FOR VISIBILITY:
       * - slice.length > 0: We have items to show (not empty state)
       * - visibleCount < adsToShow.length: More items available to reveal
       *
       * HOW THIS WORKS:
       * - IntersectionObserver (line 1225) watches this sentinel element
       * - When user scrolls near bottom, sentinel enters viewport (with 1500px prefetch margin)
       * - Observer callback increments visibleCount by pageIncrement (24)
       * - New items appear at bottom, sentinel moves down below them
       * - Process repeats until all items revealed
       *
       * WHY SHOW "Loading more…" TEXT:
       * - Provides feedback during chunked rendering (4 × 16ms frames)
       * - Prevents user confusion ("is it broken? why did scrolling stop?")
       * - Only shows when loadingMore=true (during active render)
       * - Otherwise shows space (" ") to maintain layout height for observer
       */}
      {slice.length > 0 && visibleCount < adsToShow.length && (
        <div
          ref={sentinelRef}
          className="w-full flex items-center justify-center py-6 text-sm text-muted-foreground"
        >
          {loadingMore ? "Loading more…" : " "}
        </div>
      )}

      {/**
       * Server pagination sentinel: Triggers parent to fetch next page from API.
       *
       * CONDITIONS FOR VISIBILITY:
       * - visibleCount >= adsToShow.length: We've revealed all currently-loaded items
       * - hasMoreFromServer: Parent indicates more pages available from server
       *
       * HOW THIS DIFFERS FROM CLIENT-SIDE SENTINEL (above):
       * - Client-side: Reveals already-loaded items (instant)
       * - Server-side: Fetches new items from API (slow, network-dependent)
       *
       * Only appears when we've exhausted current data and need to fetch more.
       * See serverSentinelRef IntersectionObserver setup (line 1024) for full details.
       */}
      {visibleCount >= adsToShow.length && hasMoreFromServer && (
        <div ref={serverSentinelRef} className="h-10 w-full" />
      )}

      {/**
       * Subscription paywall modal: Appears at bottom of page for non-subscribers.
       *
       * VISIBILITY LOGIC:
       * - entitlementLoaded: Only show after we've confirmed subscription status
       *   (prevents flash if user actually has access)
       * - !hasFullAccess: User doesn't have subscription or bypass flag
       *
       * WHAT IT DOES:
       * - Shows persistent footer modal explaining subscription benefits
       * - Provides CTA buttons (handleModalAction determines Login vs Subscribe)
       * - Follows user as they scroll (sticky footer)
       * - context="library": Customizes messaging for library page context
       */}
      <LoginSubscribeFooterModal
        visible={entitlementLoaded && !hasFullAccess}
        onLoginOrCreateAccount={handleModalAction}
        onSubscribe={handleModalAction}
        context="library"
      />
    </div>
  );
}
